<template>
  <div
    class="slow-drama-detail"
    :class="{ 'no-chat-layout': activeSection === 'dialogue_script' }"
    :style="layoutStyle"
    v-loading="isSectionLoading"
  >
    <aside
      v-show="activeSection !== 'dialogue_script'"
      v-loading="isChatPanelLoading"
      element-loading-text="聊天记录加载中..."
      class="left-chat-panel"
    >
      <div class="left-header">{{ leftHeaderTitle }}</div>
      <div ref="chatBodyRef" class="chat-body">
        <div v-for="(msg, index) in chatMessages" :key="index" class="msg-row" :class="msg.role">
          <div class="msg-bubble" :class="{ loading: msg.loading }">
            <div class="msg-content" v-html="renderMessageContent(msg.content)"></div>
            <span v-if="msg.loading" class="typing-dots"><i></i><i></i><i></i></span>
          </div>
        </div>
      </div>
      <div class="chat-input-wrap">
        <div class="composer-shell">
          <el-input
            v-model="chatInput"
            type="textarea"
            :rows="3"
            resize="none"
            class="composer-input"
            placeholder="输入你的需求"
            :disabled="isChatPanelLoading"
            @keydown.enter.prevent="sendMessage"
          />
          <div class="composer-footer">
            <!-- <el-button
              plain
              class="ai-assist-btn"
              :disabled="isChatPanelLoading || isChatStreaming"
            >
              AI辅助
            </el-button> -->
            <el-button
              type="primary"
              class="send-btn"
              :loading="isChatStreaming || isChatPanelLoading"
              :disabled="isChatPanelLoading || isChatStreaming || !chatInput.trim()"
              @click="sendMessage"
            >
              发送
            </el-button>
          </div>
        </div>
      </div>
      <div class="resize-handle" @mousedown="startResize" title="拖拽调整宽度">
        <span class="resize-hint">拖拽</span>
      </div>
    </aside>

    <main class="center-panel">
      <div class="center-header">
        <el-button class="back-btn" :disabled="isChatStreaming" @click="goBack">
          <el-icon><ArrowLeft /></el-icon>
          返回
        </el-button>
        <div class="center-title">
          <h2>{{ currentTitle }}</h2>
          <p>{{ project.title }}</p>
        </div>
        <el-button
          v-show="activeSection === 'synopsis'"
          type="primary"
          :loading="isSavingDetail"
          :disabled="isSavingDetail"
          @click="saveDetail"
        >
          保存
        </el-button>
        <el-button
          v-show="activeSection === 'dialogue_script'"
          type="success"
          :disabled="!hasAnyDialogueContent || isDialogueGenerating || isSectionLoading"
          @click="handleExportDialogueDocx"
        >
          导出DOCX
        </el-button>
      </div>

      <div v-show="activeSection === 'synopsis'" class="meta-strip">
        <div class="meta-card">
          <div class="meta-label">核心受众</div>
          <el-input v-model="projectMeta.coreAudience" class="meta-input" placeholder="请输入核心受众" />
        </div>
        <div class="meta-card">
          <div class="meta-label">题材</div>
          <div class="tag-input-box" @click="focusThemeInput">
            <el-tag
              v-for="item in projectMeta.theme"
              :key="`theme-${item}`"
              closable
              class="theme-tag"
              @close="removeThemeTag(item)"
            >
              {{ item }}
            </el-tag>
            <input
              ref="themeInputRef"
              v-model="themeDraft"
              class="tag-input"
              type="text"
              placeholder="输入后回车，添加题材标签"
              @keydown.enter.prevent="addThemeTag"
            />
          </div>
          <div class="tag-count">{{ projectMeta.theme.length }} / 10</div>
        </div>
      </div>

      <div v-show="activeSection === 'synopsis'" class="content-wrap">
        <section v-show="activeSection === 'synopsis'" class="content-section">
          <div class="synopsis-split">
            <div class="synopsis-panel">
              <div class="synopsis-title">短剧规划书</div>
              <el-input
                v-model="seedReportText"
                type="textarea"
                resize="none"
                class="synopsis-editor"
                placeholder="在这里填写短剧规划书"
              />
            </div>
          </div>
        </section>
      </div>

      <div v-show="activeSection === 'segment_plan'" class="segment-plan-wrap">
        <div ref="segmentLayoutRef" class="segment-plan-layout">
          <div class="segment-left-pane">
            <div class="segment-toolbar">
              <div class="segment-toolbar-info">
                <div class="segment-toolbar-title">段落规划</div>
              </div>
              <div class="segment-toolbar-actions">
                <el-button type="success" round @click="addSegmentPlan">添加段落</el-button>
              </div>
            </div>
            <div ref="segmentPlanListRef" class="segment-plan-list">
              <div
                v-for="(segment, index) in segmentPlans"
                :key="segment.id || `tmp-${index}`"
                class="segment-plan-card"
              >
                <div class="segment-plan-header">
                  <span>📝 {{ segment.title }}</span>
                  <el-button
                    v-if="index > 0"
                    type="danger"
                    plain
                    size="small"
                    @click="removeSegmentPlan(index)"
                  >
                    删除段落
                  </el-button>
                </div>
                <el-input
                  v-model="segment.text"
                  type="textarea"
                  :rows="8"
                  resize="none"
                  class="segment-plan-editor"
                  placeholder="请输入该段落的规划报告"
                />
                <div class="segment-plan-footer">
                  <div class="segment-range-controls">
                    <span class="segment-range-label">集数范围：</span>
                    <el-select v-model="segment.startEpisode" class="segment-ep-select" placeholder="开始集">
                      <el-option
                        v-for="item in episodeOptions"
                        :key="`start-${segment.id}-${item}`"
                        :label="`第${item}集`"
                        :value="item"
                      />
                    </el-select>
                    <span class="segment-range-sep">至</span>
                    <el-select v-model="segment.endEpisode" class="segment-ep-select" placeholder="结束集">
                      <el-option
                        v-for="item in episodeOptions"
                        :key="`end-${segment.id}-${item}`"
                        :label="`第${item}集`"
                        :value="item"
                      />
                    </el-select>
                  </div>
                  <div class="segment-action-controls">
                    <span v-if="isSegmentGenerating(segment, index)" class="segment-generate-progress">
                    {{ getSegmentGenerateProgressText(segment, index) }}
                    </span>
                    <el-button
                      type="info"
                      plain
                      class="segment-save-btn"
                      :loading="isSavingSegmentPlans"
                      :disabled="isAnySegmentGenerating"
                      @click="saveSegmentPlansByButton"
                    >
                      保存
                    </el-button>
                    <el-button
                      v-if="shouldShowContinueButton(segment)"
                      type="success"
                      plain
                      class="segment-generate-btn"
                      :loading="isSegmentGenerating(segment, index) && segmentGenerateMode === 'empty_only'"
                      :disabled="isAnySegmentGenerating"
                      @click="generateSegmentPlan(index, 'empty_only')"
                    >
                      继续生成
                    </el-button>
                    <el-button
                      type="primary"
                      class="segment-generate-btn"
                      :loading="isSegmentGenerating(segment, index) && segmentGenerateMode === 'overwrite'"
                      :disabled="isAnySegmentGenerating"
                      @click="generateSegmentPlan(index, 'overwrite')"
                    >
                      生成
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="segment-splitter" @mousedown="startSegmentResize">
            <span class="segment-splitter-hint">拖拽</span>
          </div>

          <div class="segment-right-pane">
            <div class="segment-right-header">
              <span>分集大纲</span>
              <!-- <el-button
                type="success"
                plain
                size="small"
                class="segment-export-btn"
                :disabled="!hasExportableSegmentOutlineData"
                @click="handleExportSegmentOutlineDocx"
              >
                导出DOCX
              </el-button> -->
            </div>
            <div ref="segmentOutlinePaneRef" class="segment-right-body">
              <div v-if="hasSegmentOutlineData" class="segment-episode-list">
                <div
                  v-for="episode in visibleSegmentEpisodes"
                  :key="`segment-outline-${episode}`"
                  class="segment-episode-card"
                  :data-episode-no="episode"
                >
                  <div
                    class="segment-episode-title"
                    :class="{ collapsed: collapsedEpisodes[episode] }"
                    @click="toggleEpisodeCollapse(episode)"
                  >
                    <span class="segment-episode-collapse-arrow" :class="{ collapsed: collapsedEpisodes[episode] }">▶</span>
                    <span class="segment-episode-title-text">第{{ episode }}集</span>
                    <span class="segment-episode-word-count">{{ getTextWordCount(segmentEpisodeTexts[episode]) }} 字</span>
                    <div class="segment-episode-actions" @click.stop>
                      <el-button
                        type="warning"
                        plain
                        size="small"
                        class="segment-episode-regenerate-btn"
                        :loading="isEpisodeRegenerating(episode)"
                        :disabled="isAnySegmentGenerating || savingEpisodeNo === episode"
                        @click="regenerateEpisodeOutline(episode)"
                      >
                        重新生成
                      </el-button>
                      <el-button
                        type="info"
                        plain
                        size="small"
                        class="segment-episode-save-btn"
                        :loading="savingEpisodeNo === episode"
                        :disabled="isAnySegmentGenerating || isEpisodeRegenerating(episode)"
                        @click="saveEpisodeOutline(episode, true)"
                      >
                        保存
                      </el-button>
                    </div>
                  </div>
                  <Transition name="episode-collapse">
                    <div v-show="!collapsedEpisodes[episode]" class="segment-episode-body">
                      <el-input
                        v-model="segmentEpisodeTexts[episode]"
                        type="textarea"
                        :autosize="{ minRows: 12 }"
                        resize="none"
                        class="segment-episode-editor"
                        :disabled="isAnySegmentGenerating || isEpisodeRegenerating(episode)"
                        :placeholder="`第${episode}集的分集大纲内容...`"
                      />
                    </div>
                  </Transition>
                </div>
              </div>
              <div v-else class="segment-empty-state">暂无分集大纲，点击左侧“生成”后在此展示</div>
            </div>
          </div>
        </div>
      </div>

      <div v-show="activeSection === 'dialogue_script'" class="dialogue-script-wrap">
        <div class="dialogue-toolbar">
          <template v-if="showDialogueProgress">
            <div class="dialogue-generate-inline">
              <div class="dialogue-generate-progress-text">
                {{ dialogueProgressLabel }}：{{ dialogueGenerateProgress.done }}/{{ dialogueGenerateProgress.total }}
              </div>
              <el-progress
                :percentage="dialogueGeneratePercent"
                :stroke-width="10"
                :show-text="true"
                status="success"
              />
            </div>
          </template>
          <template v-else>
            <div class="dialogue-range-controls">
              <span class="dialogue-range-label">生成集数范围：</span>
              <el-select v-model="dialogueStartEpisode" class="dialogue-ep-select" placeholder="开始集">
                <el-option
                  v-for="item in dialogueRangeOptions"
                  :key="`dialogue-start-${item}`"
                  :label="`第${item}集`"
                  :value="item"
                />
              </el-select>
              <span class="dialogue-range-sep">至</span>
              <el-select v-model="dialogueEndEpisode" class="dialogue-ep-select" placeholder="结束集">
                <el-option
                  v-for="item in dialogueRangeOptions"
                  :key="`dialogue-end-${item}`"
                  :label="`第${item}集`"
                  :value="item"
                />
              </el-select>
            </div>
            <div class="dialogue-toolbar-actions">
              <el-button
                plain
                :disabled="isDialogueBusy || generatedDialogueEpisodeOptions.length === 0"
                @click="openDialogueBatchRegenerateDialog"
              >
                批量重新生成
              </el-button>
              <el-button type="primary" :disabled="isDialogueBusy" @click="handleDialogueBatchGenerate">
                生成
              </el-button>
            </div>
          </template>
        </div>

        <div class="dialogue-layout">
          <div class="dialogue-pane">
            <div class="dialogue-pane-title">分集大纲</div>
            <div v-if="dialogueEpisodeList.length > 0" ref="dialogueOutlineListRef" class="dialogue-episode-list">
              <div
                v-for="episode in dialogueEpisodeList"
                :key="`dialogue-outline-${episode}`"
                class="dialogue-episode-card"
                :data-episode-no="episode"
              >
                <div class="dialogue-episode-title">
                  <span class="dialogue-episode-title-text">第{{ episode }}集</span>
                  <span class="dialogue-episode-word-count">{{ getTextWordCount(segmentEpisodeTexts[episode]) }} 字</span>
                  <div class="dialogue-episode-actions">
                    <el-button
                      type="info"
                      class="dialogue-save-btn"
                      size="small"
                      :loading="savingEpisodeNo === episode"
                      :disabled="isDialogueBusy"
                      @click="saveEpisodeOutline(episode, true)"
                    >
                      保存
                    </el-button>
                    <el-button
                      type="primary"
                      class="dialogue-generate-btn"
                      size="small"
                      :loading="isDialogueBatchEpisodePending(episode)"
                      :disabled="isDialogueBusy"
                      @click="handleDialogueSingleGenerate(episode)"
                    >
                      生成
                    </el-button>
                  </div>
                </div>
                <el-input
                  v-model="segmentEpisodeTexts[episode]"
                  type="textarea"
                  :autosize="{ minRows: 8 }"
                  resize="none"
                  class="dialogue-outline-editor"
                  :placeholder="`第${episode}集的分集大纲内容...`"
                />
              </div>
            </div>
            <div v-else class="segment-empty-state">暂无可展示集数，请先调整集数范围或生成分集大纲</div>
          </div>

          <div class="dialogue-pane">
            <div class="dialogue-pane-title">对话稿</div>
            <div v-if="dialogueEpisodeList.length > 0" ref="dialogueScriptListRef" class="dialogue-episode-list">
              <div
                v-for="episode in dialogueEpisodeList"
                :key="`dialogue-script-${episode}`"
                class="dialogue-episode-card"
                :data-episode-no="episode"
              >
                <div class="dialogue-episode-title">
                  <span class="dialogue-episode-title-text">第{{ episode }}集</span>
                  <span class="dialogue-episode-word-count">{{ getTextWordCount(dialogueEpisodeTexts[episode]) }} 字</span>
                  <div class="dialogue-episode-actions">
                    <el-button
                      type="info"
                      plain
                      size="small"
                      class="dialogue-script-regenerate-btn"
                      :loading="regeneratingDialogueEpisodeNo === episode"
                      :disabled="isDialogueBusy"
                      @click="regenerateDialogueEpisode(episode)"
                    >
                      重新生成
                    </el-button>
                    <el-button
                      type="primary"
                      size="small"
                      class="dialogue-script-save-btn"
                      :loading="savingDialogueEpisodeNo === episode"
                      :disabled="isDialogueBusy"
                      @click="saveDialogueEpisode(episode, true)"
                    >
                      保存
                    </el-button>
                  </div>
                </div>
                <el-input
                  v-model="dialogueEpisodeTexts[episode]"
                  type="textarea"
                  :autosize="{ minRows: 12 }"
                  resize="none"
                  class="dialogue-script-editor"
                  :disabled="isDialogueBusy || isDialogueEpisodeGenerating(episode)"
                  :placeholder="`第${episode}集的对话稿内容...`"
                />
              </div>
            </div>
            <div v-else class="segment-empty-state">暂无可展示集数，请先调整集数范围或生成分集大纲</div>
          </div>
        </div>
      </div>
    </main>

    <el-dialog
      v-model="showDialogueBatchRegenerateDialog"
      title="批量重新生成对话稿"
      width="720px"
      :close-on-click-modal="false"
      :close-on-press-escape="!isDialogueBatchRegenerating"
      :show-close="!isDialogueBatchRegenerating"
      destroy-on-close
    >
      <div class="dialogue-batch-regenerate-dialog">
        <div class="dialogue-batch-regenerate-tip">
          只能选择已有对话稿内容的集数，确认后会按集数从小到大依次重新生成。
        </div>
        <div class="dialogue-batch-regenerate-layout">
          <div class="dialogue-batch-regenerate-left">
            <div class="dialogue-batch-regenerate-panel-title">选择集数</div>
            <el-checkbox-group v-model="dialogueBatchRegenerateSelectedEpisodes" class="dialogue-batch-regenerate-group">
              <el-checkbox
                v-for="episode in generatedDialogueEpisodeOptions"
                :key="`dialogue-batch-regenerate-${episode}`"
                :label="episode"
                :disabled="isDialogueBatchRegenerating"
              >
                第{{ episode }}集
              </el-checkbox>
            </el-checkbox-group>
          </div>

          <div class="dialogue-batch-regenerate-right">
            <div class="dialogue-batch-regenerate-panel-title">重新生成意见</div>
            <template v-if="sortedDialogueBatchSelectedEpisodes.length > 0">
              <div
                v-for="episode in sortedDialogueBatchSelectedEpisodes"
                :key="`dialogue-batch-opinion-${episode}`"
                class="dialogue-batch-regenerate-item"
              >
                <div class="dialogue-batch-regenerate-item-title">第{{ episode }}集重新生成意见</div>
                <el-input
                  v-model="dialogueBatchRegenerateOpinionMap[episode]"
                  type="textarea"
                  :rows="4"
                  resize="none"
                  :disabled="isDialogueBatchRegenerating"
                  :placeholder="`例如：第${episode}集冲突更强、对白更短促、突出主角主动反击...`"
                />
              </div>
            </template>
            <div v-else class="dialogue-batch-regenerate-empty">
              请先在左侧选择需要重新生成的集数
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <span class="dialogue-batch-regenerate-footer">
          <el-button :disabled="isDialogueBatchRegenerating" @click="showDialogueBatchRegenerateDialog = false">取消</el-button>
          <el-button
            type="primary"
            :loading="isDialogueBatchRegenerating"
            :disabled="sortedDialogueBatchSelectedEpisodes.length === 0"
            @click="confirmDialogueBatchRegenerate"
          >
            一键批量重新生成
          </el-button>
        </span>
      </template>
    </el-dialog>

    <aside class="right-nav-panel">
      <div
        v-for="item in navItems"
        :key="item.id"
        class="nav-item"
        :class="{ active: activeSection === item.id, disabled: isNavSwitchLocked }"
        @click="handleNavClick(item.id)"
      >
        {{ item.title }}
      </div>
    </aside>

    <div
      class="ai-assist-floating-layer"
      :style="aiAssistLayerStyle"
    >
      <transition name="ai-assist-panel-fade">
        <div
          v-if="showAiAssistPanel"
          class="ai-assist-panel"
          :class="[`align-${aiAssistSnapSide}`]"
          :style="aiAssistPanelStyle"
          @mousedown.stop
        >
          <div class="ai-assist-panel-header">
            <div class="ai-assist-header-main">
              <div class="ai-assist-avatar">AI</div>
              <div class="ai-assist-header-meta">
                <div class="ai-assist-panel-title">爆款短剧的资深大编剧</div>
                <div class="ai-assist-panel-subtitle">可帮你评审并给出修改建议</div>
              </div>
            </div>
            <el-button plain class="ai-assist-close-btn" @click="showAiAssistPanel = false">收起</el-button>
          </div>
          <div class="ai-assist-panel-body">
            <div
              ref="aiAssistChatBodyRef"
              v-loading="isAiAssistLoading"
              element-loading-text="AI辅助加载中..."
              class="ai-assist-chat-preview"
            >
              <div
                v-for="(item, index) in aiAssistMessages"
                :key="`ai-assist-preview-${index}`"
                class="ai-assist-preview-row"
                :class="item.role"
              >
                <div class="ai-assist-preview-bubble" :class="{ loading: item.loading }">
                  <div
                    v-if="item.content"
                    class="ai-assist-rich-content"
                    v-html="renderMessageContent(item.content)"
                  ></div>
                  <div
                    v-if="item.loading"
                    class="ai-assist-bubble-loading"
                    :class="{ inline: !!(item.content || '').trim() }"
                  >
                    <span v-if="!(item.content || '').trim()" class="ai-assist-bubble-loading-text">AI辅助思考中</span>
                    <span class="ai-assist-bubble-loading-dots" aria-hidden="true">
                      <i></i>
                      <i></i>
                      <i></i>
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="!aiAssistLoadingOrStreaming && aiAssistMessages.length === 0" class="ai-assist-empty-state">
                {{ aiAssistEmptyText }}
              </div>
            </div>
            <div class="ai-assist-input-card">
              <el-input
                v-model="aiAssistDraft"
                type="textarea"
                :rows="4"
                resize="none"
                :disabled="!isAiAssistAvailable || isAiAssistLoading || isAiAssistStreaming"
                :placeholder="aiAssistInputPlaceholder"
                @keydown.enter.prevent="sendAiAssistMessage"
              />
              <div class="ai-assist-footer-actions">
                <el-button
                  type="primary"
                  :loading="isAiAssistStreaming"
                  :disabled="!isAiAssistAvailable || isAiAssistLoading || isAiAssistStreaming || !aiAssistDraft.trim()"
                  @click="sendAiAssistMessage"
                >
                  发送
                </el-button>
              </div>
            </div>
          </div>
          <div class="ai-assist-resize-handle" @mousedown.stop.prevent="startAiAssistResize"></div>
        </div>
      </transition>

      <button
        type="button"
        class="ai-assist-fab"
        :class="{ dragging: isAiAssistDragging }"
        @mousedown.stop.prevent="startAiAssistDrag"
        @click.stop="toggleAiAssistPanel"
      >
        <span class="ai-assist-fab-avatar">编</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onActivated, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ArrowLeft } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import request from '@/api/request';
import { getSlowDramaWorkflowConfig, getSlowDramaWorkflowTypeFromRoute } from '@/config/slowDramaWorkflows';

const route = useRoute();
const router = useRouter();

const workflowType = computed(() => getSlowDramaWorkflowTypeFromRoute(route));
const workflowConfig = computed(() => getSlowDramaWorkflowConfig(workflowType.value));
const isCurrentWorkflowDetailRoute = computed(() => String(route.name || '') === String(workflowConfig.value.detailRouteName || ''));

const SLOW_DRAMA_PROJECT_ID_KEY = computed(() => `${workflowConfig.value.workflowType}CurrentProjectId`);
const SLOW_DRAMA_PROJECT_TITLE_KEY = computed(() => `${workflowConfig.value.workflowType}CurrentProjectTitle`);
const LEGACY_SLOW_DRAMA_PROJECT_ID_KEY = computed(() => (
  workflowType.value === 'sweet_romance' ? 'sweetDramaCurrentProjectId' : 'slowDramaCurrentProjectId'
));
const LEGACY_SLOW_DRAMA_PROJECT_TITLE_KEY = computed(() => (
  workflowType.value === 'sweet_romance' ? 'sweetDramaCurrentProjectTitle' : 'slowDramaCurrentProjectTitle'
));

const defaultTitle = computed(() => workflowConfig.value.defaultProjectTitle);

const getStoredProjectId = () => (
  localStorage.getItem(SLOW_DRAMA_PROJECT_ID_KEY.value) ||
  localStorage.getItem(LEGACY_SLOW_DRAMA_PROJECT_ID_KEY.value) ||
  ''
);

const getStoredProjectTitle = () => (
  localStorage.getItem(SLOW_DRAMA_PROJECT_TITLE_KEY.value) ||
  localStorage.getItem(LEGACY_SLOW_DRAMA_PROJECT_TITLE_KEY.value) ||
  ''
);

const persistProjectCache = (projectId, projectTitle) => {
  const normalizedId = Number(projectId || 0);
  if (normalizedId > 0) {
    const idValue = String(normalizedId);
    localStorage.setItem(SLOW_DRAMA_PROJECT_ID_KEY.value, idValue);
    localStorage.setItem(LEGACY_SLOW_DRAMA_PROJECT_ID_KEY.value, idValue);
  }
  if (projectTitle) {
    const titleValue = String(projectTitle);
    localStorage.setItem(SLOW_DRAMA_PROJECT_TITLE_KEY.value, titleValue);
    localStorage.setItem(LEGACY_SLOW_DRAMA_PROJECT_TITLE_KEY.value, titleValue);
  }
};

const project = ref({
  id: route.params.id || route.query.id || getStoredProjectId() || '',
  title: route.query.title || getStoredProjectTitle() || defaultTitle.value
});

const syncProjectFromRoute = () => {
  const nextId = route.params.id || route.query.id || getStoredProjectId() || project.value.id;
  const nextTitle = route.query.title || getStoredProjectTitle() || project.value.title || defaultTitle.value;
  const changed = String(project.value.id) !== String(nextId) || String(project.value.title) !== String(nextTitle);
  project.value = {
    ...project.value,
    id: nextId,
    title: nextTitle
  };
  persistProjectCache(nextId, nextTitle);
  return changed;
};

const navItems = [
  { id: 'synopsis', title: '短剧规划书' },
  { id: 'segment_plan', title: '段落规划' },
  { id: 'dialogue_script', title: '对话稿' }
];

const activeSection = ref('synopsis');
const leftPanelWidth = ref(Math.floor(window.innerWidth * 0.24));
const isResizing = ref(false);
const resizeStartX = ref(0);
const resizeStartWidth = ref(280);
const segmentLayoutRef = ref(null);
const segmentPlanListRef = ref(null);
const segmentOutlinePaneRef = ref(null);
const dialogueOutlineListRef = ref(null);
const dialogueScriptListRef = ref(null);
const segmentSplitPercent = ref(46);
const isSegmentResizing = ref(false);
const segmentResizeStartX = ref(0);
const segmentResizeStartPercent = ref(46);

const seedReportText = ref('');
const savedSeedReport = ref('');
const createDefaultSegmentPlan = () => ({ id: null, title: '段落1', text: '', startEpisode: 1, endEpisode: 10 });
const segmentPlans = ref([createDefaultSegmentPlan()]);
const segmentEpisodeTexts = ref({});
const segmentGeneratingId = ref(null);
const segmentPreparingIndex = ref(null);
const segmentGenerateMode = ref('');
const segmentGenerateProgress = reactive({
  done: 0,
  total: 0,
  startEpisode: 0,
  endEpisode: 0,
  currentEpisode: 0
});
const isSegmentPreparing = ref(false);
const isSavingSegmentPlans = ref(false);
const savingEpisodeNo = ref(null);
const pendingEpisodeSaveNotifyMap = ref({});
const regeneratingEpisodeNos = reactive(new Set());
const isEpisodeRegenerating = (ep) => regeneratingEpisodeNos.has(ep);
const getCurrentRegeneratingEpisodeNo = () => {
  const iterator = regeneratingEpisodeNos.values().next();
  return iterator.done ? 0 : Number(iterator.value || 0);
};
const collapsedEpisodes = ref({});
const toggleEpisodeCollapse = (episode) => {
  collapsedEpisodes.value = {
    ...collapsedEpisodes.value,
    [episode]: !collapsedEpisodes.value[episode]
  };
};
const dialogueStartEpisode = ref(1);
const dialogueEndEpisode = ref(10);
const dialogueEpisodeTexts = ref({});
const savingDialogueEpisodeNo = ref(null);
const pendingDialogueSaveNotifyMap = ref({});
const regeneratingDialogueEpisodeNo = ref(null);
const dialogueGenerateMode = ref('');
const dialogueGenerateProgress = reactive({
  done: 0,
  total: 0
});
const dialogueGeneratingEpisodes = ref({});
const showDialogueBatchRegenerateDialog = ref(false);
const isDialogueBatchRegenerating = ref(false);
const dialogueBatchRegenerateSelectedEpisodes = ref([]);
const dialogueBatchRegenerateOpinionMap = ref({});
const dialogueGeneratingRange = reactive({
  start: 0,
  end: 0
});

const projectMeta = ref({
  coreAudience: '',
  theme: []
});
const themeDraft = ref('');
const themeInputRef = ref(null);
const chatBodyRef = ref(null);
const chatMessages = ref([]);
const chatInput = ref('');
const isChatStreaming = ref(false);
const isInitializingChat = ref(false);
const aiAssistChatBodyRef = ref(null);
const aiAssistMessages = ref([]);
const isAiAssistLoading = ref(false);
const isAiAssistStreaming = ref(false);
const isSavingDetail = ref(false);
const isSectionLoading = ref(false);
const skipFirstActivatedLoad = ref(true);
let activeSectionLoadPromise = null;
let activeSectionLoadKey = '';
const savedSegmentPlansSnapshot = ref([]);
const savedSegmentEpisodeTextsSnapshot = ref({});
const savedDialogueEpisodeTextsSnapshot = ref({});
const showAiAssistPanel = ref(false);
const aiAssistDraft = ref('');
const aiAssistPosition = reactive({
  x: 0,
  y: 140
});
const aiAssistPanelSize = reactive({
  width: 480,
  height: 680
});
const aiAssistSnapSide = ref('right');
const isAiAssistDragging = ref(false);
const isAiAssistResizing = ref(false);
const aiAssistDragMoved = ref(false);
const aiAssistDragStart = reactive({
  mouseX: 0,
  mouseY: 0,
  x: 0,
  y: 0
});
const aiAssistResizeStart = reactive({
  mouseX: 0,
  mouseY: 0,
  width: 480,
  height: 680
});

const currentTitle = computed(() => {
  const map = {
    synopsis: '短剧规划书',
    segment_plan: '段落规划',
    dialogue_script: '对话稿'
  };
  return map[activeSection.value];
});

const leftHeaderTitle = computed(() => {
  const map = {
    synopsis: workflowConfig.value.synopsisAssistantTitle,
    segment_plan: workflowConfig.value.segmentAssistantTitle,
    dialogue_script: workflowConfig.value.dialogueAssistantTitle
  };
  return map[activeSection.value] || workflowConfig.value.synopsisAssistantTitle;
});

const currentChatScene = computed(() => (
  activeSection.value === 'segment_plan' ? 'segment_plan' : 'synopsis'
));
const currentAiAssistScene = computed(() => (
  activeSection.value === 'segment_plan'
    ? 'segment_plan'
    : (activeSection.value === 'dialogue_script'
      ? 'dialogue_script'
      : (activeSection.value === 'synopsis' ? 'synopsis' : ''))
));
const isAiAssistAvailable = computed(() => (
  currentAiAssistScene.value === 'synopsis' ||
  currentAiAssistScene.value === 'segment_plan' ||
  currentAiAssistScene.value === 'dialogue_script'
));
const aiAssistLoadingOrStreaming = computed(() => isAiAssistLoading.value || isAiAssistStreaming.value);
const aiAssistInputPlaceholder = computed(() => {
  if (currentAiAssistScene.value === 'dialogue_script') {
    return '比如：帮我评审当前对话稿哪里有问题，或直接给我一句发给主智能体的修改意见。';
  }
  if (currentAiAssistScene.value === 'segment_plan') {
    return '比如：帮我写一句回复给段落规划大师，或评估当前分集大纲哪里偏了。';
  }
  if (currentAiAssistScene.value === 'synopsis') {
    return '比如：帮我写一句回复给主智能体，让短剧规划书推进得更好。';
  }
  return '当前先接入短剧规划书页和段落规划页，其他页面的 AI辅助稍后再接。';
});
const aiAssistEmptyText = computed(() => {
  if (currentAiAssistScene.value === 'dialogue_script') {
    return '我会结合短剧规划书和每一集的对话稿，帮你评审问题并整理可直接执行的修改建议。';
  }
  if (currentAiAssistScene.value === 'segment_plan') {
    return '我会结合短剧规划书、段落规划、已生成分集大纲和主聊天记录，帮你整理更适合发给段落规划大师的回复。';
  }
  if (currentAiAssistScene.value === 'synopsis') {
    return `我会结合“${currentTitle.value}”当前内容，帮你整理更适合发给主智能体的回复。`;
  }
  return '当前先接入短剧规划书、段落规划和对话稿页的 AI辅助，切回对应页面即可使用。';
});

const episodeOptions = computed(() => Array.from({ length: 60 }, (_, i) => i + 1));
const visibleSegmentEpisodes = computed(() => (
  Object.keys(segmentEpisodeTexts.value || {})
    .map((episode) => Number(episode))
    .filter((episode) => episode > 0)
    .sort((a, b) => a - b)
));
const hasSegmentOutlineData = computed(() => visibleSegmentEpisodes.value.length > 0);
const hasExportableSegmentOutlineData = computed(() => (
  visibleSegmentEpisodes.value.some((episode) => String(segmentEpisodeTexts.value[episode] || '').trim() !== '')
));
const isAnySegmentGenerating = computed(() => isSegmentPreparing.value || segmentGeneratingId.value !== null);
const isSegmentEpisodeRegenerating = computed(() => regeneratingEpisodeNos.size > 0);
const isDialogueEpisodeRegenerating = computed(() => Number(regeneratingDialogueEpisodeNo.value || 0) > 0);
const generatedDialogueEpisodeOptions = computed(() => (
  dialogueEpisodeList.value
    .filter((episode) => String(dialogueEpisodeTexts.value[episode] || '').trim() !== '')
    .sort((a, b) => a - b)
));
const sortedDialogueBatchSelectedEpisodes = computed(() => (
  [...dialogueBatchRegenerateSelectedEpisodes.value]
    .map((episode) => Number(episode || 0))
    .filter((episode) => episode > 0)
    .sort((a, b) => a - b)
));
const isDialogueGenerating = computed(() => dialogueGeneratingRange.start > 0 && dialogueGeneratingRange.end >= dialogueGeneratingRange.start);
const showDialogueProgress = computed(() => (
  (isDialogueGenerating.value || isDialogueBatchRegenerating.value) &&
  Number(dialogueGenerateProgress.total || 0) > 0
));
const dialogueProgressLabel = computed(() => (
  isDialogueBatchRegenerating.value ? '批量重新生成进度' : '生成进度'
));
const isDialogueBusy = computed(() => (
  isDialogueGenerating.value ||
  isDialogueEpisodeRegenerating.value ||
  isDialogueBatchRegenerating.value
));
const isNavSwitchLocked = computed(() => (
  isChatStreaming.value ||
  (activeSection.value === 'segment_plan' && (isAnySegmentGenerating.value || isSegmentEpisodeRegenerating.value)) ||
  (activeSection.value === 'dialogue_script' && isDialogueBusy.value)
));
const isChatPanelLoading = computed(() => (
  activeSection.value !== 'dialogue_script' && (isSectionLoading.value || isInitializingChat.value)
));
const hasAnyDialogueContent = computed(() => (
  Object.keys(dialogueEpisodeTexts.value || {}).some((episode) => {
    const text = String(dialogueEpisodeTexts.value[episode] || '').trim();
    return Number(episode) > 0 && text !== '';
  })
));
const isDialogueEpisodeGenerating = (episodeNo) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return false;
  if (Number(regeneratingDialogueEpisodeNo.value || 0) === targetEpisode) {
    return true;
  }
  if (!isDialogueGenerating.value && !isDialogueBatchRegenerating.value) return false;
  return dialogueGeneratingEpisodes.value[targetEpisode] === true;
};
const isDialogueBatchEpisodePending = (episodeNo) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode || !isDialogueBatchRegenerating.value) return false;
  return dialogueGeneratingEpisodes.value[targetEpisode] === true;
};
const dialogueDisplayEpisodeRange = computed(() => {
  let minEpisode = 0;
  let maxEpisode = 0;
  segmentPlans.value.forEach((segment) => {
    const startEpisode = Number(segment?.startEpisode || 0);
    const endEpisode = Number(segment?.endEpisode || 0);
    if (!startEpisode || !endEpisode || startEpisode > endEpisode) return;
    if (minEpisode === 0 || startEpisode < minEpisode) {
      minEpisode = startEpisode;
    }
    if (endEpisode > maxEpisode) {
      maxEpisode = endEpisode;
    }
  });
  if (!minEpisode || !maxEpisode || minEpisode > maxEpisode) {
    return [1, 10];
  }
  return [minEpisode, maxEpisode];
});
const dialogueEpisodeList = computed(() => {
  const [startEpisode, endEpisode] = dialogueDisplayEpisodeRange.value;
  if (!startEpisode || !endEpisode || startEpisode > endEpisode) {
    return [];
  }
  return episodeOptions.value.filter((episode) => episode >= startEpisode && episode <= endEpisode);
});
const dialogueGeneratePercent = computed(() => {
  const total = Number(dialogueGenerateProgress.total || 0);
  const done = Number(dialogueGenerateProgress.done || 0);
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
});
const dialogueRangeOptions = computed(() => {
  const [minEpisode, maxEpisode] = dialogueDisplayEpisodeRange.value;
  if (!minEpisode || !maxEpisode || minEpisode > maxEpisode) {
    return [];
  }
  return episodeOptions.value.filter((episode) => episode >= minEpisode && episode <= maxEpisode);
});

const getTextWordCount = (text) => String(text || '').trim().length;
const getSegmentTotalCount = (segment) => {
  const startEpisode = Number(segment?.startEpisode || 0);
  const endEpisode = Number(segment?.endEpisode || 0);
  if (!startEpisode || !endEpisode || startEpisode > endEpisode) return 0;
  return endEpisode - startEpisode + 1;
};
const getSegmentCompletedCount = (segment) => {
  const startEpisode = Number(segment?.startEpisode || 0);
  const endEpisode = Number(segment?.endEpisode || 0);
  if (!startEpisode || !endEpisode || startEpisode > endEpisode) return 0;
  let count = 0;
  for (let episode = startEpisode; episode <= endEpisode; episode++) {
    const text = String(segmentEpisodeTexts.value[episode] || '').trim();
    if (text) {
      count++;
    }
  }
  return count;
};
const shouldShowContinueButton = (segment) => {
  const total = getSegmentTotalCount(segment);
  if (total <= 0) return false;
  const completed = getSegmentCompletedCount(segment);
  return completed > 0 && completed < total;
};
const isSegmentPreparingTarget = (index) => (
  isSegmentPreparing.value && Number(segmentPreparingIndex.value) === Number(index)
);
const isSegmentGenerating = (segment, index) => {
  const segmentId = Number(segment?.id || 0);
  if (segmentId > 0 && Number(segmentGeneratingId.value || 0) === segmentId) {
    return true;
  }
  return isSegmentPreparingTarget(index);
};
const getSegmentGenerateProgressText = (segment, index) => {
  if (isSegmentPreparingTarget(index)) {
    return '准备生成中...';
  }
  return `生成中 ${segmentGenerateProgress.currentEpisode || Number(segment?.startEpisode || 0)}/${segmentGenerateProgress.endEpisode || Number(segment?.endEpisode || 0)}集`;
};
const findSegmentByEpisode = (episodeNo) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return null;
  return segmentPlans.value.find((segment) => {
    const startEpisode = Number(segment.startEpisode || 0);
    const endEpisode = Number(segment.endEpisode || 0);
    return targetEpisode >= startEpisode && targetEpisode <= endEpisode;
  }) || null;
};

const cloneSegmentPlansForSnapshot = (plans) => (
  (Array.isArray(plans) ? plans : []).map((item, index) => ({
    id: Number(item?.id || 0) || null,
    title: String(item?.title || `段落${index + 1}`),
    text: String(item?.text || ''),
    startEpisode: Number(item?.startEpisode || 0),
    endEpisode: Number(item?.endEpisode || 0)
  }))
);

const cloneEpisodeTextMap = (map) => {
  const source = map && typeof map === 'object' ? map : {};
  const next = {};
  Object.keys(source).forEach((key) => {
    const episodeNo = Number(key);
    if (episodeNo > 0) {
      next[episodeNo] = String(source[key] ?? '');
    }
  });
  return next;
};

const getDirtyEpisodeList = (currentMap, savedMap) => {
  const current = cloneEpisodeTextMap(currentMap);
  const saved = cloneEpisodeTextMap(savedMap);
  const keySet = new Set([...Object.keys(current), ...Object.keys(saved)]);
  return Array.from(keySet)
    .map((key) => Number(key))
    .filter((episodeNo) => episodeNo > 0 && String(current[episodeNo] || '') !== String(saved[episodeNo] || ''))
    .sort((a, b) => a - b);
};

const getDirtySegmentTitles = () => {
  const current = cloneSegmentPlansForSnapshot(segmentPlans.value);
  const saved = cloneSegmentPlansForSnapshot(savedSegmentPlansSnapshot.value);
  const maxLen = Math.max(current.length, saved.length);
  const dirty = [];
  for (let i = 0; i < maxLen; i++) {
    const currentItem = current[i];
    const savedItem = saved[i];
    if (!currentItem || !savedItem) {
      const title = currentItem?.title || savedItem?.title || `段落${i + 1}`;
      dirty.push(title);
      continue;
    }
    if (
      Number(currentItem.id || 0) !== Number(savedItem.id || 0) ||
      String(currentItem.title || '') !== String(savedItem.title || '') ||
      String(currentItem.text || '') !== String(savedItem.text || '') ||
      Number(currentItem.startEpisode || 0) !== Number(savedItem.startEpisode || 0) ||
      Number(currentItem.endEpisode || 0) !== Number(savedItem.endEpisode || 0)
    ) {
      dirty.push(currentItem.title || savedItem.title || `段落${i + 1}`);
    }
  }
  return dirty;
};

const toEpisodeLabelList = (episodes) => (
  (Array.isArray(episodes) ? episodes : [])
    .map((episodeNo) => `第${Number(episodeNo)}集`)
    .slice(0, 8)
);

const getLatestEpisodeNoFromTextMap = (map) => {
  if (!map || typeof map !== 'object') return 0;
  let maxEpisode = 0;
  Object.keys(map).forEach((key) => {
    const episodeNo = Number(key);
    if (episodeNo > 0 && String(map[episodeNo] || '').trim() !== '') {
      if (episodeNo > maxEpisode) {
        maxEpisode = episodeNo;
      }
    }
  });
  return maxEpisode;
};

const scrollDialogueListsToEpisode = async (episodeNo) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return;
  await nextTick();
  const scrollOne = (container) => {
    if (!container) return;
    const card = container.querySelector(`.dialogue-episode-card[data-episode-no="${targetEpisode}"]`);
    if (!card) return;
    // Use container scrollTop to avoid scrolling the whole page.
    const offsetTop = card.offsetTop;
    const pad = 6;
    container.scrollTop = Math.max(0, offsetTop - pad);
  };
  scrollOne(dialogueOutlineListRef.value);
  scrollOne(dialogueScriptListRef.value);
  requestAnimationFrame(() => {
    scrollOne(dialogueOutlineListRef.value);
    scrollOne(dialogueScriptListRef.value);
  });
};

const resizeDialogueOutlineEditors = async () => {
  await nextTick();
  const container = dialogueOutlineListRef.value;
  if (!container) return;
  const textareas = container.querySelectorAll('.dialogue-outline-editor textarea');
  textareas.forEach((textarea) => {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  });
};

const applySegmentPlanData = (data) => {
  const segments = Array.isArray(data?.segments) ? data.segments : [];
  const nextSegments = segments.map((item, index) => ({
    id: Number(item.id || 0) || null,
    title: item.title || `段落${index + 1}`,
    text: item.plan_text || '',
    startEpisode: Number(item.start_episode || 1),
    endEpisode: Number(item.end_episode || 1)
  }));
  if (nextSegments.length === 0) {
    segmentPlans.value = [createDefaultSegmentPlan()];
  } else {
    segmentPlans.value = nextSegments;
  }
  const outlines = Array.isArray(data?.episode_outlines) ? data.episode_outlines : [];
  const nextTexts = {};
  outlines.forEach((item) => {
    const episodeNo = Number(item.episode_no || 0);
    if (episodeNo <= 0) return;
    nextTexts[episodeNo] = item.outline_text || '';
  });
  segmentEpisodeTexts.value = nextTexts;
  savedSegmentPlansSnapshot.value = cloneSegmentPlansForSnapshot(segmentPlans.value);
  savedSegmentEpisodeTextsSnapshot.value = cloneEpisodeTextMap(segmentEpisodeTexts.value);
  if (activeSection.value === 'dialogue_script') {
    resizeDialogueOutlineEditors();
  }
};

const loadSegmentPlanData = async () => {
  const projectId = Number(project.value.id);
  if (!projectId) return;
  try {
    const data = await request.post('/slowDrama/segment_plan_init', {
      project_id: projectId
    });
    applySegmentPlanData(data || {});
  } catch (error) {
    ElMessage.error('段落规划加载失败');
  }
};

const saveSegmentPlans = async (notifySuccess = false) => {
  const projectId = Number(project.value.id);
  if (!projectId) return false;
  if (isSavingSegmentPlans.value) return false;
  isSavingSegmentPlans.value = true;
  const payloadSegments = segmentPlans.value.map((item, index) => ({
    id: Number(item.id || 0),
    segment_no: index + 1,
    title: String(item.title || `段落${index + 1}`).trim() || `段落${index + 1}`,
    plan_text: String(item.text || ''),
    start_episode: Number(item.startEpisode || 1),
    end_episode: Number(item.endEpisode || 1),
    sort_order: index
  }));
  try {
    const data = await request.post('/slowDrama/segment_plan_save', {
      project_id: projectId,
      segments: JSON.stringify(payloadSegments)
    });
    applySegmentPlanData(data || {});
    if (notifySuccess) {
      ElMessage.success('段落保存成功');
    }
    return true;
  } catch (error) {
    ElMessage.error(error?.message || '段落保存失败');
    return false;
  } finally {
    isSavingSegmentPlans.value = false;
  }
};

const saveSegmentPlansByButton = async () => {
  await saveSegmentPlans(true);
};

const saveEpisodeOutline = async (episodeNo, notifySuccess = false) => {
  const targetSegment = findSegmentByEpisode(episodeNo);
  if (!targetSegment || !targetSegment.id) return;
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return;
  if (Number(savingEpisodeNo.value || 0) === targetEpisode) {
    if (notifySuccess) {
      pendingEpisodeSaveNotifyMap.value[targetEpisode] = true;
      pendingEpisodeSaveNotifyMap.value = { ...pendingEpisodeSaveNotifyMap.value };
    }
    return;
  }
  const text = String(segmentEpisodeTexts.value[episodeNo] || '');
  savingEpisodeNo.value = targetEpisode;
  const shouldNotifyOnSuccess = Boolean(notifySuccess || pendingEpisodeSaveNotifyMap.value[targetEpisode]);
  if (pendingEpisodeSaveNotifyMap.value[targetEpisode]) {
    const nextMap = { ...pendingEpisodeSaveNotifyMap.value };
    delete nextMap[targetEpisode];
    pendingEpisodeSaveNotifyMap.value = nextMap;
  }
  try {
    await request.post('/slowDrama/episode_outline_save', {
      project_id: Number(project.value.id),
      items: JSON.stringify([{
        segment_id: Number(targetSegment.id),
        episode_no: targetEpisode,
        outline_text: text
      }])
    });
    savedSegmentEpisodeTextsSnapshot.value[targetEpisode] = String(segmentEpisodeTexts.value[targetEpisode] || '');
    savedSegmentEpisodeTextsSnapshot.value = { ...savedSegmentEpisodeTextsSnapshot.value };
    if (shouldNotifyOnSuccess) {
      ElMessage.success(`第${targetEpisode}集保存成功`);
    }
  } catch (error) {
    ElMessage.warning(`第${targetEpisode}集内容保存失败，请稍后重试`);
  } finally {
    savingEpisodeNo.value = null;
  }
};
const regenerateEpisodeOutline = async (episodeNo) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return;
  if (isAnySegmentGenerating.value) {
    ElMessage.warning('大纲正在生成中，请稍后再试');
    return;
  }
  if (isEpisodeRegenerating(targetEpisode)) return;
  const currentRegeneratingEpisode = getCurrentRegeneratingEpisodeNo();
  if (currentRegeneratingEpisode > 0) {
    ElMessage.warning(`第${currentRegeneratingEpisode}集大纲正在生成中`);
    return;
  }
  let inputValue = '';
  try {
    const promptResult = await ElMessageBox.prompt(
      `可选填写第${targetEpisode}集的补充要求，系统会结合对应段落与上一集内容重新生成`,
      `重新生成第${targetEpisode}集`,
      {
        confirmButtonText: '开始生成',
        cancelButtonText: '取消',
        inputType: 'textarea',
        inputPlaceholder: '例如：本集冲突更强、节奏更快，突出主角主动反击...',
        inputValue: ''
      }
    );
    inputValue = String(promptResult.value || '').trim();
  } catch (error) {
    return;
  }
  const previousOutlineText = String(segmentEpisodeTexts.value[targetEpisode] || '');
  let hasReceivedNewContent = false;
  regeneratingEpisodeNos.add(targetEpisode);
  segmentEpisodeTexts.value[targetEpisode] = '';
  segmentEpisodeTexts.value = { ...segmentEpisodeTexts.value };
  try {
    const response = await request.streamRequest('/slowDrama/episode_outline_regenerate', {
      project_id: Number(project.value.id),
      episode_no: targetEpisode,
      custom_prompt: inputValue
    }, { timeout: 1800000, silent: true });
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('流式通道不可用');
    }
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = 'message';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) {
          currentEvent = 'message';
          continue;
        }
        if (trimmedLine.startsWith('event:')) {
          currentEvent = trimmedLine.slice(6).trim() || 'message';
          continue;
        }
        if (!trimmedLine.startsWith('data:')) {
          continue;
        }
        const rawData = trimmedLine.slice(5).trim();
        if (!rawData || rawData === '[DONE]') {
          continue;
        }
        let payload = null;
        try {
          payload = JSON.parse(rawData);
        } catch (error) {
          payload = { content: rawData };
        }
        if (!payload) continue;
        if (payload.heartbeat) continue;
        if (currentEvent === 'error' || payload.error) {
          throw new Error(payload.message || payload.error || '生成失败');
        }
        if (currentEvent === 'episode_delta') {
          const delta = String(payload.content || '');
          if (delta) {
            hasReceivedNewContent = true;
            segmentEpisodeTexts.value[targetEpisode] = String(segmentEpisodeTexts.value[targetEpisode] || '') + delta;
            segmentEpisodeTexts.value = { ...segmentEpisodeTexts.value };
            scrollSegmentEpisodeTextareaToBottom(targetEpisode);
          }
          continue;
        }
        if (currentEvent === 'episode_done') {
          hasReceivedNewContent = true;
          segmentEpisodeTexts.value[targetEpisode] = payload.outline_text || '';
          segmentEpisodeTexts.value = { ...segmentEpisodeTexts.value };
          savedSegmentEpisodeTextsSnapshot.value[targetEpisode] = String(payload.outline_text || '');
          savedSegmentEpisodeTextsSnapshot.value = { ...savedSegmentEpisodeTextsSnapshot.value };
          continue;
        }
      }
    }
    ElMessage.success(`第${targetEpisode}集重新生成完成`);
    await refreshBalance();
  } catch (error) {
    if (!hasReceivedNewContent) {
      segmentEpisodeTexts.value[targetEpisode] = previousOutlineText;
      segmentEpisodeTexts.value = { ...segmentEpisodeTexts.value };
    }
    // 错误提示由 request 统一处理
  } finally {
    regeneratingEpisodeNos.delete(targetEpisode);
  }
};

const applyDialogueScriptData = (data) => {
  const items = Array.isArray(data?.items) ? data.items : [];
  const nextTexts = {};
  items.forEach((item) => {
    const episodeNo = Number(item.episode_no || 0);
    if (episodeNo <= 0) return;
    nextTexts[episodeNo] = item.dialogue_text || '';
  });
  dialogueEpisodeTexts.value = nextTexts;
  savedDialogueEpisodeTextsSnapshot.value = cloneEpisodeTextMap(dialogueEpisodeTexts.value);
};
const loadDialogueScriptData = async () => {
  const projectId = Number(project.value.id);
  if (!projectId) return;
  try {
    const data = await request.post('/slowDrama/dialogue_script_init', {
      project_id: projectId
    });
    applyDialogueScriptData(data || {});
  } catch (error) {
    ElMessage.error('对话稿加载失败');
  }
};
const saveDialogueEpisode = async (episodeNo, notifySuccess = false) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return;
  if (Number(savingDialogueEpisodeNo.value || 0) === targetEpisode) {
    if (notifySuccess) {
      pendingDialogueSaveNotifyMap.value[targetEpisode] = true;
      pendingDialogueSaveNotifyMap.value = { ...pendingDialogueSaveNotifyMap.value };
    }
    return;
  }
  const targetSegment = findSegmentByEpisode(targetEpisode);
  const segmentId = Number(targetSegment?.id || 0);
  const text = String(dialogueEpisodeTexts.value[targetEpisode] || '');
  savingDialogueEpisodeNo.value = targetEpisode;
  const shouldNotifyOnSuccess = Boolean(notifySuccess || pendingDialogueSaveNotifyMap.value[targetEpisode]);
  if (pendingDialogueSaveNotifyMap.value[targetEpisode]) {
    const nextMap = { ...pendingDialogueSaveNotifyMap.value };
    delete nextMap[targetEpisode];
    pendingDialogueSaveNotifyMap.value = nextMap;
  }
  try {
    await request.post('/slowDrama/dialogue_script_save', {
      project_id: Number(project.value.id),
      items: JSON.stringify([{
        segment_id: segmentId,
        episode_no: targetEpisode,
        dialogue_text: text
      }])
    });
    savedDialogueEpisodeTextsSnapshot.value[targetEpisode] = String(dialogueEpisodeTexts.value[targetEpisode] || '');
    savedDialogueEpisodeTextsSnapshot.value = { ...savedDialogueEpisodeTextsSnapshot.value };
    if (shouldNotifyOnSuccess) {
      ElMessage.success(`第${targetEpisode}集对话稿保存成功`);
    }
  } catch (error) {
    ElMessage.warning(`第${targetEpisode}集对话稿保存失败，请稍后重试`);
  } finally {
    savingDialogueEpisodeNo.value = null;
  }
};
const runDialogueEpisodeRegenerate = async (episodeNo, inputValue = '', successMessage = '', options = {}) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return;
  const customPrompt = String(inputValue || '').trim();
  const preserveBatchState = Boolean(options.preserveBatchState);
  regeneratingDialogueEpisodeNo.value = targetEpisode;
  if (!preserveBatchState) {
    dialogueGeneratingEpisodes.value = { [targetEpisode]: true };
  }
  const previousDialogueText = String(dialogueEpisodeTexts.value[targetEpisode] || '');
  let hasReceivedNewContent = false;
  if (!preserveBatchState) {
    dialogueEpisodeTexts.value[targetEpisode] = '';
    dialogueEpisodeTexts.value = { ...dialogueEpisodeTexts.value };
  }
  try {
    const response = await request.streamRequest('/slowDrama/dialogue_script_generate_stream', {
      project_id: Number(project.value.id),
      start_episode: targetEpisode,
      end_episode: targetEpisode,
      mode: 'overwrite',
      custom_prompt: customPrompt
    }, { timeout: 1800000, silent: true });
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('流式通道不可用');
    }
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = 'message';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) {
          currentEvent = 'message';
          continue;
        }
        if (trimmedLine.startsWith('event:')) {
          currentEvent = trimmedLine.slice(6).trim() || 'message';
          continue;
        }
        if (!trimmedLine.startsWith('data:')) {
          continue;
        }
        const rawData = trimmedLine.slice(5).trim();
        if (!rawData || rawData === '[DONE]') {
          continue;
        }
        let payload = null;
        try {
          payload = JSON.parse(rawData);
        } catch (error) {
          payload = { content: rawData };
        }
        if (!payload || payload.heartbeat) continue;
        if (currentEvent === 'error' || payload.error) {
          throw new Error(payload.message || payload.error || '生成失败');
        }
        if (currentEvent === 'episode_start') {
          dialogueEpisodeTexts.value[targetEpisode] = '';
          dialogueEpisodeTexts.value = { ...dialogueEpisodeTexts.value };
          continue;
        }
        if (currentEvent === 'episode_delta') {
          const delta = String(payload.content || '');
          if (delta) {
            hasReceivedNewContent = true;
            dialogueEpisodeTexts.value[targetEpisode] = String(dialogueEpisodeTexts.value[targetEpisode] || '') + delta;
            dialogueEpisodeTexts.value = { ...dialogueEpisodeTexts.value };
            scrollDialogueEpisodeTextareaToBottom(targetEpisode);
          }
          continue;
        }
        if (currentEvent === 'episode_done') {
          hasReceivedNewContent = true;
          dialogueEpisodeTexts.value[targetEpisode] = payload.dialogue_text || '';
          dialogueEpisodeTexts.value = { ...dialogueEpisodeTexts.value };
          savedDialogueEpisodeTextsSnapshot.value[targetEpisode] = String(payload.dialogue_text || '');
          savedDialogueEpisodeTextsSnapshot.value = { ...savedDialogueEpisodeTextsSnapshot.value };
          if (dialogueGeneratingEpisodes.value[targetEpisode]) {
            const nextMap = { ...dialogueGeneratingEpisodes.value };
            delete nextMap[targetEpisode];
            dialogueGeneratingEpisodes.value = nextMap;
            syncDialogueProgress();
          }
          continue;
        }
      }
    }
    if (successMessage) {
      ElMessage.success(successMessage);
    }
    await refreshBalance();
    return true;
  } catch (error) {
    if (!hasReceivedNewContent) {
      dialogueEpisodeTexts.value[targetEpisode] = previousDialogueText;
      dialogueEpisodeTexts.value = { ...dialogueEpisodeTexts.value };
    }
    return false;
  } finally {
    if (!preserveBatchState) {
      dialogueGeneratingEpisodes.value = {};
    }
    regeneratingDialogueEpisodeNo.value = null;
  }
};

const regenerateDialogueEpisode = async (episodeNo) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return;
  if (isDialogueGenerating.value || isDialogueEpisodeRegenerating.value || isDialogueBatchRegenerating.value) {
    ElMessage.warning('对话稿正在生成中，请稍后再试');
    return;
  }
  let inputValue = '';
  try {
    const promptResult = await ElMessageBox.prompt(
      `可选填写第${targetEpisode}集的补充要求，系统会结合对应分集大纲与上一集对话稿重新生成`,
      `重新生成第${targetEpisode}集对话稿`,
      {
        confirmButtonText: '开始生成',
        cancelButtonText: '取消',
        inputType: 'textarea',
        inputPlaceholder: '例如：本集冲突更强、对话更短促，突出主角主动反击...',
        inputValue: ''
      }
    );
    inputValue = String(promptResult.value || '').trim();
  } catch (error) {
    return;
  }
  await runDialogueEpisodeRegenerate(targetEpisode, inputValue, `第${targetEpisode}集对话稿重新生成完成`);
};
const getDialogueCompletedCount = (startEpisode, endEpisode) => {
  let count = 0;
  for (let episode = Number(startEpisode || 0); episode <= Number(endEpisode || 0); episode++) {
    const text = String(dialogueEpisodeTexts.value[episode] || '').trim();
    if (text) count++;
  }
  return count;
};
const syncDialogueProgress = () => {
  const remaining = Object.keys(dialogueGeneratingEpisodes.value || {}).length;
  const total = Number(dialogueGenerateProgress.total || 0);
  dialogueGenerateProgress.done = Math.max(0, total - remaining);
};
const generateDialogueByRange = async (startEpisode, endEpisode, mode = 'empty_only') => {
  if (isDialogueGenerating.value) {
    ElMessage.warning('已有对话稿正在生成，请稍后');
    return;
  }
  if (!savedSeedReport.value.trim()) {
    ElMessage.warning('请先保存短剧规划书后再生成');
    return;
  }
  const start = Number(startEpisode || 0);
  const end = Number(endEpisode || 0);
  const [minEpisode, maxEpisode] = dialogueDisplayEpisodeRange.value;
  if (!minEpisode || !maxEpisode || minEpisode > maxEpisode) {
    ElMessage.warning('请先完成有效的分集大纲范围配置');
    return;
  }
  if (!start || !end || start > end) {
    ElMessage.warning('开始集不能大于结束集');
    return;
  }
  if (start < minEpisode || end > maxEpisode) {
    ElMessage.warning(`生成范围必须在第${minEpisode}集至第${maxEpisode}集之间`);
    return;
  }

  dialogueGeneratingRange.start = start;
  dialogueGeneratingRange.end = end;
  const nextGeneratingEpisodes = {};
  for (let episode = start; episode <= end; episode++) {
    nextGeneratingEpisodes[episode] = true;
  }
  dialogueGeneratingEpisodes.value = nextGeneratingEpisodes;
  dialogueGenerateMode.value = mode === 'overwrite' ? 'overwrite' : 'empty_only';
  dialogueGenerateProgress.done = 0;
  dialogueGenerateProgress.total = end - start + 1;
  const isRetryableDialogueStreamError = (error) => {
    const msg = String(error?.message || '');
    if (!msg) return false;
    return (
      msg.includes('请求超时') ||
      msg.includes('网络') ||
      msg.includes('Network') ||
      msg.includes('fetch') ||
      msg.includes('aborted') ||
      msg.includes('连接中断')
    );
  };
  try {
    const maxReconnectRetries = 5;
    for (let attempt = 0; attempt <= maxReconnectRetries; attempt++) {
      const pendingEpisodes = Object.keys(dialogueGeneratingEpisodes.value || {})
        .map((item) => Number(item))
        .filter((item) => item > 0)
        .sort((a, b) => a - b);
      if (pendingEpisodes.length === 0) break;
      const requestStart = pendingEpisodes[0];
      const requestEnd = pendingEpisodes[pendingEpisodes.length - 1];
      const requestMode = attempt === 0 ? dialogueGenerateMode.value : 'empty_only';
      let reader = null;
      try {
        const response = await request.streamRequest('/slowDrama/dialogue_script_generate_stream', {
          project_id: Number(project.value.id),
          start_episode: requestStart,
          end_episode: requestEnd,
          mode: requestMode
        }, { timeout: 1800000, silent: true });
        reader = response.body?.getReader();
        if (!reader) {
          throw new Error('流式通道不可用');
        }
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = 'message';
        const handleDialogueSseEvent = (eventName, payload) => {
          if (!payload) return;
          if (payload.heartbeat) return;
          if (eventName === 'error' || payload.error) {
            const message = payload.message || payload.error || '生成失败';
            throw new Error(message);
          }
          if (eventName === 'episode_start') {
            const episodeNo = Number(payload.episode_no || 0);
            if (episodeNo > 0) {
              if (requestMode === 'overwrite' || attempt > 0) {
                dialogueEpisodeTexts.value[episodeNo] = '';
              } else if (!Object.prototype.hasOwnProperty.call(dialogueEpisodeTexts.value, episodeNo)) {
                dialogueEpisodeTexts.value[episodeNo] = '';
              }
              dialogueEpisodeTexts.value = { ...dialogueEpisodeTexts.value };
            }
            return;
          }
          if (eventName === 'episode_delta') {
            const episodeNo = Number(payload.episode_no || 0);
            const delta = String(payload.content || '');
            if (episodeNo > 0 && delta) {
              dialogueEpisodeTexts.value[episodeNo] = String(dialogueEpisodeTexts.value[episodeNo] || '') + delta;
              dialogueEpisodeTexts.value = { ...dialogueEpisodeTexts.value };
              scrollDialogueEpisodeTextareaToBottom(episodeNo);
            }
            return;
          }
          if (eventName === 'episode_done') {
            const episodeNo = Number(payload.episode_no || 0);
            if (episodeNo > 0) {
              dialogueEpisodeTexts.value[episodeNo] = payload.dialogue_text || '';
              dialogueEpisodeTexts.value = { ...dialogueEpisodeTexts.value };
              savedDialogueEpisodeTextsSnapshot.value[episodeNo] = String(payload.dialogue_text || '');
              savedDialogueEpisodeTextsSnapshot.value = { ...savedDialogueEpisodeTextsSnapshot.value };
              if (dialogueGeneratingEpisodes.value[episodeNo]) {
                const nextMap = { ...dialogueGeneratingEpisodes.value };
                delete nextMap[episodeNo];
                dialogueGeneratingEpisodes.value = nextMap;
                syncDialogueProgress();
              }
            }
            return;
          }
          if (eventName === 'episode_skip') {
            const episodeNo = Number(payload.episode_no || 0);
            if (episodeNo > 0 && dialogueGeneratingEpisodes.value[episodeNo]) {
              const nextMap = { ...dialogueGeneratingEpisodes.value };
              delete nextMap[episodeNo];
              dialogueGeneratingEpisodes.value = nextMap;
              syncDialogueProgress();
            }
            return;
          }
          if (eventName === 'done' && payload.ok) {
            syncDialogueProgress();
          }
        };
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine) {
              currentEvent = 'message';
              continue;
            }
            if (trimmedLine.startsWith('event:')) {
              currentEvent = trimmedLine.slice(6).trim() || 'message';
              continue;
            }
            if (!trimmedLine.startsWith('data:')) {
              continue;
            }
            const rawData = trimmedLine.slice(5).trim();
            if (!rawData || rawData === '[DONE]') {
              continue;
            }
            let payload = null;
            try {
              payload = JSON.parse(rawData);
            } catch (error) {
              payload = { content: rawData };
            }
            handleDialogueSseEvent(currentEvent, payload);
          }
        }
      } catch (error) {
        const hasPending = Object.keys(dialogueGeneratingEpisodes.value || {}).length > 0;
        const canRetry = attempt < maxReconnectRetries && hasPending && isRetryableDialogueStreamError(error);
        if (canRetry) {
          // 静默自动重试：不打断用户感知
          await new Promise((resolve) => setTimeout(resolve, 1200 * (attempt + 1)));
          continue;
        }
        throw error;
      } finally {
        if (reader && typeof reader.cancel === 'function') {
          try {
            await reader.cancel();
          } catch (error) {
            // ignore
          }
        }
      }
    }
    ElMessage.success(dialogueGenerateMode.value === 'overwrite' ? '覆盖生成完成' : '继续生成完成');
  } catch (error) {
    ElMessage.warning(error?.message || '生成中断，可稍后重试');
  } finally {
    await refreshBalance();
    dialogueGeneratingEpisodes.value = {};
    dialogueGeneratingRange.start = 0;
    dialogueGeneratingRange.end = 0;
    dialogueGenerateMode.value = '';
    dialogueGenerateProgress.done = 0;
    dialogueGenerateProgress.total = 0;
  }
};
const handleDialogueBatchGenerate = async () => {
  const start = Number(dialogueStartEpisode.value || 0);
  const end = Number(dialogueEndEpisode.value || 0);
  if (!start || !end || start > end) {
    ElMessage.warning('开始集不能大于结束集');
    return;
  }
  const completed = getDialogueCompletedCount(start, end);
  if (completed > 0) {
    try {
      await ElMessageBox.confirm(
        `第${start}~${end}集已有${completed}集存在内容，请选择生成方式`,
        '选择生成方式',
        {
          type: 'warning',
          confirmButtonText: '覆盖生成',
          cancelButtonText: '补空生成',
          distinguishCancelAndClose: true
        }
      );
      await generateDialogueByRange(start, end, 'overwrite');
      return;
    } catch (error) {
      if (error === 'cancel') {
        await generateDialogueByRange(start, end, 'empty_only');
        return;
      }
      return;
    }
  }
  await generateDialogueByRange(start, end, 'overwrite');
};
const handleDialogueSingleGenerate = async (episodeNo) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return;
  const existingText = String(dialogueEpisodeTexts.value[targetEpisode] || '').trim();
  if (existingText) {
    try {
      await ElMessageBox.confirm(
        `第${targetEpisode}集已有对话稿，是否覆盖生成？`,
        '确认覆盖生成',
        {
          type: 'warning',
          confirmButtonText: '确认覆盖',
          cancelButtonText: '取消'
        }
      );
    } catch (error) {
      return;
    }
  }
  await generateDialogueByRange(targetEpisode, targetEpisode, 'overwrite');
};

const openDialogueBatchRegenerateDialog = () => {
  if (isDialogueGenerating.value || isDialogueEpisodeRegenerating.value || isDialogueBatchRegenerating.value) {
    ElMessage.warning('对话稿正在生成中，请稍后再试');
    return;
  }
  if (generatedDialogueEpisodeOptions.value.length === 0) {
    ElMessage.warning('当前还没有可重新生成的对话稿');
    return;
  }
  dialogueBatchRegenerateSelectedEpisodes.value = [];
  dialogueBatchRegenerateOpinionMap.value = {};
  showDialogueBatchRegenerateDialog.value = true;
};

const confirmDialogueBatchRegenerate = async () => {
  if (isDialogueGenerating.value || isDialogueEpisodeRegenerating.value || isDialogueBatchRegenerating.value) {
    ElMessage.warning('对话稿正在生成中，请稍后再试');
    return;
  }
  const selectedEpisodes = sortedDialogueBatchSelectedEpisodes.value.filter((episode) => (
    generatedDialogueEpisodeOptions.value.includes(episode)
  ));
  if (selectedEpisodes.length === 0) {
    ElMessage.warning('请至少选择一集已生成的对话稿');
    return;
  }
  showDialogueBatchRegenerateDialog.value = false;
  isDialogueBatchRegenerating.value = true;
  const previousDialogueTextMap = {};
  const nextGeneratingEpisodes = {};
  selectedEpisodes.forEach((episode) => {
    previousDialogueTextMap[episode] = String(dialogueEpisodeTexts.value[episode] || '');
    nextGeneratingEpisodes[episode] = true;
    dialogueEpisodeTexts.value[episode] = '';
  });
  dialogueEpisodeTexts.value = { ...dialogueEpisodeTexts.value };
  dialogueGeneratingEpisodes.value = nextGeneratingEpisodes;
  dialogueGenerateProgress.done = 0;
  dialogueGenerateProgress.total = selectedEpisodes.length;
  syncDialogueProgress();
  try {
    for (const episode of selectedEpisodes) {
      const opinion = String(dialogueBatchRegenerateOpinionMap.value[episode] || '').trim();
      const ok = await runDialogueEpisodeRegenerate(episode, opinion, '', { preserveBatchState: true });
      if (!ok) {
        const pendingEpisodes = Object.keys(dialogueGeneratingEpisodes.value || {})
          .map((item) => Number(item || 0))
          .filter((item) => item > 0);
        pendingEpisodes.forEach((pendingEpisode) => {
          if (Object.prototype.hasOwnProperty.call(previousDialogueTextMap, pendingEpisode)) {
            dialogueEpisodeTexts.value[pendingEpisode] = previousDialogueTextMap[pendingEpisode];
          }
        });
        dialogueEpisodeTexts.value = { ...dialogueEpisodeTexts.value };
        ElMessage.warning(`批量重新生成在第${episode}集中断，请检查后重试`);
        break;
      }
    }
    if (Object.keys(dialogueGeneratingEpisodes.value || {}).length === 0) {
      ElMessage.success(`已按顺序完成第${selectedEpisodes.join('、')}集对话稿重新生成`);
    }
  } finally {
    dialogueGeneratingEpisodes.value = {};
    dialogueGenerateProgress.done = 0;
    dialogueGenerateProgress.total = 0;
    isDialogueBatchRegenerating.value = false;
  }
};

const addSegmentPlan = () => {
  const nextNumber = segmentPlans.value.length + 1;
  segmentPlans.value.push({
    id: null,
    title: `段落${nextNumber}`,
    text: '',
    startEpisode: 1,
    endEpisode: 10
  });
};

const removeSegmentPlan = (index) => {
  if (segmentPlans.value.length <= 1) {
    ElMessage.warning('至少保留一个段落');
    return;
  }
  segmentPlans.value.splice(index, 1);
  segmentPlans.value = segmentPlans.value.map((item, idx) => ({
    ...item,
    title: `段落${idx + 1}`
  }));
};

const generateSegmentPlan = async (index, mode = 'overwrite') => {
  const segment = segmentPlans.value[index];
  if (!segment) return;
  if (isAnySegmentGenerating.value) {
    ElMessage.warning('已有段落正在生成，请稍后');
    return;
  }
  const startEpisode = Number(segment.startEpisode || 0);
  const endEpisode = Number(segment.endEpisode || 0);
  if (!startEpisode || !endEpisode || startEpisode > endEpisode) {
    ElMessage.warning('开始集不能大于结束集');
    return;
  }
  const text = String(segment.text || '').trim();
  if (!text) {
    ElMessage.warning('请先填写段落内容');
    return;
  }
  if (!savedSeedReport.value.trim()) {
    ElMessage.warning('请先保存短剧规划书后再生成');
    return;
  }
  const completed = getSegmentCompletedCount(segment);
  const total = getSegmentTotalCount(segment);
  if (mode === 'overwrite' && completed > 0) {
    try {
      await ElMessageBox.confirm(
        `将覆盖第${startEpisode}~${endEpisode}集已生成/已修改内容，是否继续？`,
        '确认覆盖生成',
        {
          type: 'warning',
          confirmButtonText: '确认覆盖',
          cancelButtonText: '取消'
        }
      );
    } catch (error) {
      return;
    }
  }
  isSegmentPreparing.value = true;
  segmentPreparingIndex.value = index;
  segmentGenerateMode.value = mode === 'empty_only' ? 'empty_only' : 'overwrite';
  const saved = await saveSegmentPlans();
  if (!saved) {
    isSegmentPreparing.value = false;
    segmentPreparingIndex.value = null;
    segmentGenerateMode.value = '';
    return;
  }
  const latestSegment = segmentPlans.value[index];
  if (!latestSegment || !latestSegment.id) {
    isSegmentPreparing.value = false;
    segmentPreparingIndex.value = null;
    segmentGenerateMode.value = '';
    ElMessage.error('段落ID无效，请重试');
    return;
  }

  isSegmentPreparing.value = false;
  segmentPreparingIndex.value = null;
  segmentGeneratingId.value = Number(latestSegment.id);
  segmentGenerateProgress.done = 0;
  segmentGenerateProgress.total = total;
  segmentGenerateProgress.startEpisode = startEpisode;
  segmentGenerateProgress.endEpisode = endEpisode;
  segmentGenerateProgress.currentEpisode = startEpisode;
  try {
    const maxReconnectRetries = 5;
    const isRetryableError = (error) => {
      const message = String(error?.message || '');
      if (error?.isTimeout || error?.isNetworkError) return true;
      if (error?.name === 'TypeError') return true;
      return /Failed to fetch|NetworkError|ERR_HTTP2_PROTOCOL_ERROR|fetch|Load failed|terminated|network/i.test(message);
    };
    let completed = false;
    for (let attempt = 0; attempt <= maxReconnectRetries; attempt += 1) {
      try {
        const requestMode = attempt === 0 ? segmentGenerateMode.value : 'empty_only';
        const response = await request.streamRequest('/slowDrama/segment_outline_generate_stream', {
          project_id: Number(project.value.id),
          segment_id: Number(latestSegment.id),
          mode: requestMode
        }, { timeout: 1800000, silent: true });
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('流式通道不可用');
        }
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = 'message';
        const updateProgressByPayload = (payload) => {
          const doneCount = Number(payload?.done_count || 0);
          const skipCount = Number(payload?.skip_count || 0);
          if (Number.isFinite(doneCount) && Number.isFinite(skipCount)) {
            segmentGenerateProgress.done = Math.max(segmentGenerateProgress.done, doneCount + skipCount);
          }
        };
        const handleSegmentSseEvent = (eventName, payload) => {
          if (!payload) return;
          if (payload.heartbeat) return;
          if (eventName === 'error' || payload.error) {
            const message = payload.message || payload.error || '生成失败';
            throw new Error(message);
          }
          if (eventName === 'meta') {
            const eventStart = Number(payload.start_episode || 0);
            const eventEnd = Number(payload.end_episode || 0);
            if (eventStart > 0 && eventEnd >= eventStart) {
              segmentGenerateProgress.total = eventEnd - eventStart + 1;
              segmentGenerateProgress.startEpisode = eventStart;
              segmentGenerateProgress.endEpisode = eventEnd;
              if (
                !segmentGenerateProgress.currentEpisode ||
                segmentGenerateProgress.currentEpisode < eventStart ||
                segmentGenerateProgress.currentEpisode > eventEnd
              ) {
                segmentGenerateProgress.currentEpisode = eventStart;
              }
            }
            return;
          }
          if (eventName === 'episode_start') {
            const episodeNo = Number(payload.episode_no || 0);
            if (episodeNo > 0) {
              segmentGenerateProgress.currentEpisode = episodeNo;
              if (requestMode === 'overwrite' || attempt > 0) {
                segmentEpisodeTexts.value[episodeNo] = '';
              } else if (!Object.prototype.hasOwnProperty.call(segmentEpisodeTexts.value, episodeNo)) {
                segmentEpisodeTexts.value[episodeNo] = '';
              }
              segmentEpisodeTexts.value = { ...segmentEpisodeTexts.value };
              scrollSegmentEpisodeCardIntoView(episodeNo);
            }
            return;
          }
          if (eventName === 'episode_delta') {
            const episodeNo = Number(payload.episode_no || 0);
            const delta = String(payload.content || '');
            if (episodeNo > 0 && delta) {
              segmentEpisodeTexts.value[episodeNo] = String(segmentEpisodeTexts.value[episodeNo] || '') + delta;
              segmentEpisodeTexts.value = { ...segmentEpisodeTexts.value };
              scrollSegmentEpisodeTextareaToBottom(episodeNo);
            }
            return;
          }
          if (eventName === 'episode_done') {
            const episodeNo = Number(payload.episode_no || 0);
            if (episodeNo > 0) {
              segmentEpisodeTexts.value[episodeNo] = payload.outline_text || '';
              segmentEpisodeTexts.value = { ...segmentEpisodeTexts.value };
              savedSegmentEpisodeTextsSnapshot.value[episodeNo] = String(payload.outline_text || '');
              savedSegmentEpisodeTextsSnapshot.value = { ...savedSegmentEpisodeTextsSnapshot.value };
            }
            updateProgressByPayload(payload);
            return;
          }
          if (eventName === 'episode_skip') {
            updateProgressByPayload(payload);
            return;
          }
          if (eventName === 'done') {
            if (payload.ok) {
              segmentGenerateProgress.done = segmentGenerateProgress.total;
            }
          }
        };
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine) {
              currentEvent = 'message';
              continue;
            }
            if (trimmedLine.startsWith('event:')) {
              currentEvent = trimmedLine.slice(6).trim() || 'message';
              continue;
            }
            if (!trimmedLine.startsWith('data:')) {
              continue;
            }
            const rawData = trimmedLine.slice(5).trim();
            if (!rawData || rawData === '[DONE]') {
              continue;
            }
            let payload = null;
            try {
              payload = JSON.parse(rawData);
            } catch (error) {
              payload = { content: rawData };
            }
            handleSegmentSseEvent(currentEvent, payload);
          }
        }
        completed = true;
        break;
      } catch (streamError) {
        if (attempt < maxReconnectRetries && isRetryableError(streamError)) {
          // 静默自动重试：不打断用户感知
          await new Promise((resolve) => setTimeout(resolve, 1200 * (attempt + 1)));
          continue;
        }
        throw streamError;
      }
    }
    if (!completed) {
      throw new Error('生成中断，可点击继续生成');
    }
    ElMessage.success(mode === 'empty_only' ? '继续生成完成' : '覆盖生成完成');
  } catch (error) {
    ElMessage.warning(error?.message || '生成中断，可点击继续生成');
  } finally {
    await refreshBalance();
    isSegmentPreparing.value = false;
    segmentPreparingIndex.value = null;
    segmentGeneratingId.value = null;
    segmentGenerateMode.value = '';
    segmentGenerateProgress.done = 0;
    segmentGenerateProgress.total = 0;
    segmentGenerateProgress.startEpisode = 0;
    segmentGenerateProgress.endEpisode = 0;
    segmentGenerateProgress.currentEpisode = 0;
  }
};

const goBack = () => {
  router.push(workflowConfig.value.listPath);
};

const saveDirtyEpisodeOutlines = async (dirtyEpisodes) => {
  const episodeList = (Array.isArray(dirtyEpisodes) ? dirtyEpisodes : [])
    .map((item) => Number(item))
    .filter((item) => item > 0);
  if (episodeList.length === 0) return true;
  const items = [];
  episodeList.forEach((episodeNo) => {
    const segment = findSegmentByEpisode(episodeNo);
    const segmentId = Number(segment?.id || 0);
    if (segmentId <= 0) return;
    items.push({
      segment_id: segmentId,
      episode_no: episodeNo,
      outline_text: String(segmentEpisodeTexts.value[episodeNo] || '')
    });
  });
  if (items.length === 0) return true;
  try {
    await request.post('/slowDrama/episode_outline_save', {
      project_id: Number(project.value.id),
      items: JSON.stringify(items)
    });
    const nextSnapshot = cloneEpisodeTextMap(savedSegmentEpisodeTextsSnapshot.value);
    episodeList.forEach((episodeNo) => {
      nextSnapshot[episodeNo] = String(segmentEpisodeTexts.value[episodeNo] || '');
    });
    savedSegmentEpisodeTextsSnapshot.value = nextSnapshot;
    return true;
  } catch (error) {
    ElMessage.error('分集大纲保存失败，请稍后重试');
    return false;
  }
};

const saveDirtyDialogueEpisodes = async (dirtyEpisodes) => {
  const episodeList = (Array.isArray(dirtyEpisodes) ? dirtyEpisodes : [])
    .map((item) => Number(item))
    .filter((item) => item > 0);
  if (episodeList.length === 0) return true;
  const items = episodeList.map((episodeNo) => {
    const segment = findSegmentByEpisode(episodeNo);
    return {
      segment_id: Number(segment?.id || 0),
      episode_no: episodeNo,
      dialogue_text: String(dialogueEpisodeTexts.value[episodeNo] || '')
    };
  });
  try {
    await request.post('/slowDrama/dialogue_script_save', {
      project_id: Number(project.value.id),
      items: JSON.stringify(items)
    });
    const nextSnapshot = cloneEpisodeTextMap(savedDialogueEpisodeTextsSnapshot.value);
    episodeList.forEach((episodeNo) => {
      nextSnapshot[episodeNo] = String(dialogueEpisodeTexts.value[episodeNo] || '');
    });
    savedDialogueEpisodeTextsSnapshot.value = nextSnapshot;
    return true;
  } catch (error) {
    ElMessage.error('对话稿保存失败，请稍后重试');
    return false;
  }
};

const buildUnsavedChangesForCurrentSection = () => {
  if (activeSection.value === 'synopsis') {
    const hasUnsavedSeedReport = seedReportText.value.trim() !== savedSeedReport.value.trim();
    return {
      hasChanges: hasUnsavedSeedReport,
      lines: hasUnsavedSeedReport ? ['短剧规划书内容未保存'] : []
    };
  }
  if (activeSection.value === 'segment_plan') {
    const dirtySegments = getDirtySegmentTitles();
    const dirtyOutlines = getDirtyEpisodeList(segmentEpisodeTexts.value, savedSegmentEpisodeTextsSnapshot.value);
    const lines = [];
    if (dirtySegments.length > 0) {
      lines.push(`段落未保存：${dirtySegments.slice(0, 6).join('、')}`);
    }
    if (dirtyOutlines.length > 0) {
      const labels = toEpisodeLabelList(dirtyOutlines);
      const suffix = dirtyOutlines.length > labels.length ? ` 等${dirtyOutlines.length}集` : '';
      lines.push(`分集大纲未保存：${labels.join('、')}${suffix}`);
    }
    return { hasChanges: lines.length > 0, lines };
  }
  if (activeSection.value === 'dialogue_script') {
    const dirtyOutlines = getDirtyEpisodeList(segmentEpisodeTexts.value, savedSegmentEpisodeTextsSnapshot.value);
    const dirtyDialogues = getDirtyEpisodeList(dialogueEpisodeTexts.value, savedDialogueEpisodeTextsSnapshot.value);
    const lines = [];
    if (dirtyOutlines.length > 0) {
      const labels = toEpisodeLabelList(dirtyOutlines);
      const suffix = dirtyOutlines.length > labels.length ? ` 等${dirtyOutlines.length}集` : '';
      lines.push(`分集大纲未保存：${labels.join('、')}${suffix}`);
    }
    if (dirtyDialogues.length > 0) {
      const labels = toEpisodeLabelList(dirtyDialogues);
      const suffix = dirtyDialogues.length > labels.length ? ` 等${dirtyDialogues.length}集` : '';
      lines.push(`对话稿未保存：${labels.join('、')}${suffix}`);
    }
    return { hasChanges: lines.length > 0, lines };
  }
  return { hasChanges: false, lines: [] };
};

const saveCurrentSectionChanges = async () => {
  if (activeSection.value === 'synopsis') {
    return saveDetail();
  }
  if (activeSection.value === 'segment_plan') {
    const dirtySegments = getDirtySegmentTitles();
    if (dirtySegments.length > 0) {
      const saved = await saveSegmentPlans(false);
      if (!saved) return false;
      savedSegmentPlansSnapshot.value = cloneSegmentPlansForSnapshot(segmentPlans.value);
      savedSegmentEpisodeTextsSnapshot.value = cloneEpisodeTextMap(segmentEpisodeTexts.value);
      return true;
    }
    const dirtyOutlines = getDirtyEpisodeList(segmentEpisodeTexts.value, savedSegmentEpisodeTextsSnapshot.value);
    return saveDirtyEpisodeOutlines(dirtyOutlines);
  }
  if (activeSection.value === 'dialogue_script') {
    const dirtyOutlines = getDirtyEpisodeList(segmentEpisodeTexts.value, savedSegmentEpisodeTextsSnapshot.value);
    if (dirtyOutlines.length > 0) {
      const outlineSaved = await saveDirtyEpisodeOutlines(dirtyOutlines);
      if (!outlineSaved) return false;
    }
    const dirtyDialogues = getDirtyEpisodeList(dialogueEpisodeTexts.value, savedDialogueEpisodeTextsSnapshot.value);
    return saveDirtyDialogueEpisodes(dirtyDialogues);
  }
  return true;
};

const clearChatPanelData = () => {
  chatMessages.value = [];
  chatInput.value = '';
};

const clearAiAssistData = (resetDraft = false) => {
  aiAssistMessages.value = [];
  if (resetDraft) {
    aiAssistDraft.value = '';
  }
};

const clearSectionData = (section) => {
  if (section === 'synopsis') {
    seedReportText.value = '';
    savedSeedReport.value = '';
    clearChatPanelData();
  }
  if (section === 'segment_plan') {
    clearChatPanelData();
    segmentPlans.value = [];
    segmentEpisodeTexts.value = {};
    savedSegmentEpisodeTextsSnapshot.value = {};
    savedSegmentPlansSnapshot.value = [];
  }
  if (section === 'dialogue_script') {
    dialogueEpisodeTexts.value = {};
    savedDialogueEpisodeTextsSnapshot.value = {};
  }
  clearAiAssistData();
};

const handleNavClick = async (targetSection) => {
  if (activeSection.value === 'segment_plan' && (isAnySegmentGenerating.value || isSegmentEpisodeRegenerating.value)) {
    ElMessage.warning('大纲正在生成中');
    return;
  }
  if (activeSection.value === 'dialogue_script' && (isDialogueGenerating.value || isDialogueEpisodeRegenerating.value)) {
    ElMessage.warning('对话稿正在生成中');
    return;
  }
  if (isChatStreaming.value) {
    ElMessage.warning('请等待当前回复完成后再切换');
    return;
  }
  if (targetSection === activeSection.value) return;

  const unsavedInfo = buildUnsavedChangesForCurrentSection();
  if (unsavedInfo.hasChanges) {
    const detailText = unsavedInfo.lines.join('\n');
    try {
      await ElMessageBox.confirm(
        `检测到以下内容未保存：\n${detailText}\n\n是否保存后再切换？`,
        '未保存提示',
        {
          type: 'warning',
          confirmButtonText: '保存并切换',
          cancelButtonText: '不保存直接切换',
          distinguishCancelAndClose: true
        }
      );
      const saved = await saveCurrentSectionChanges();
      if (!saved) return;
      ElMessage.success('保存成功，已切换页面');
    } catch (action) {
      if (action !== 'cancel') {
        return;
      }
    }
  }

  if ((targetSection === 'segment_plan' || targetSection === 'dialogue_script') && !savedSeedReport.value.trim()) {
    ElMessage.warning('请先保存短剧规划书，再进入段落规划/对话稿');
    return;
  }
  clearSectionData(targetSection);
  activeSection.value = targetSection;
  await loadSectionData();
};

const layoutStyle = computed(() => ({
  '--left-panel-width': `${leftPanelWidth.value}px`,
  '--segment-left-width': `${segmentSplitPercent.value}%`
}));

const aiAssistLayerStyle = computed(() => ({
  left: `${Math.round(aiAssistPosition.x)}px`,
  top: `${Math.round(aiAssistPosition.y)}px`
}));

const aiAssistPanelStyle = computed(() => ({
  width: `${Math.round(aiAssistPanelSize.width)}px`,
  height: `${Math.round(aiAssistPanelSize.height)}px`
}));

const getMinWidth = () => 220;
const getMaxWidth = () => {
  const viewportWidth = window.innerWidth || 1280;
  let ratio = 0.34;
  if (viewportWidth <= 1280) {
    ratio = 0.38;
  } else if (viewportWidth <= 1440) {
    ratio = 0.36;
  }
  return Math.max(320, Math.floor(viewportWidth * ratio));
};

const handleResizeMove = (event) => {
  if (!isResizing.value) return;
  const delta = event.clientX - resizeStartX.value;
  const nextWidth = resizeStartWidth.value + delta;
  const minWidth = getMinWidth();
  const maxWidth = getMaxWidth();
  leftPanelWidth.value = Math.min(maxWidth, Math.max(minWidth, nextWidth));
};

const stopResize = () => {
  if (!isResizing.value) return;
  isResizing.value = false;
  document.body.style.userSelect = '';
  window.removeEventListener('mousemove', handleResizeMove);
  window.removeEventListener('mouseup', stopResize);
};

const startResize = (event) => {
  if (window.innerWidth <= 1024) return;
  isResizing.value = true;
  resizeStartX.value = event.clientX;
  resizeStartWidth.value = leftPanelWidth.value;
  document.body.style.userSelect = 'none';
  window.addEventListener('mousemove', handleResizeMove);
  window.addEventListener('mouseup', stopResize);
};

const handleSegmentResizeMove = (event) => {
  if (!isSegmentResizing.value) return;
  const layoutWidth = segmentLayoutRef.value?.clientWidth || 0;
  if (layoutWidth <= 0) return;
  const delta = event.clientX - segmentResizeStartX.value;
  const deltaPercent = (delta / layoutWidth) * 100;
  const nextPercent = segmentResizeStartPercent.value + deltaPercent;
  segmentSplitPercent.value = Math.min(72, Math.max(28, nextPercent));
};

const stopSegmentResize = () => {
  if (!isSegmentResizing.value) return;
  isSegmentResizing.value = false;
  document.body.style.userSelect = '';
  window.removeEventListener('mousemove', handleSegmentResizeMove);
  window.removeEventListener('mouseup', stopSegmentResize);
  nextTick(() => refreshSegmentEpisodeTextareaHeights());
};

const refreshSegmentEpisodeTextareaHeights = () => {
  const list = document.querySelector('.segment-episode-list');
  if (!list) return;
  const textareas = list.querySelectorAll('.segment-episode-editor textarea');
  textareas.forEach((ta) => {
    ta.style.height = 'auto';
    ta.style.height = ta.scrollHeight + 'px';
  });
};

const startSegmentResize = (event) => {
  if (window.innerWidth <= 1200) return;
  isSegmentResizing.value = true;
  segmentResizeStartX.value = event.clientX;
  segmentResizeStartPercent.value = segmentSplitPercent.value;
  document.body.style.userSelect = 'none';
  window.addEventListener('mousemove', handleSegmentResizeMove);
  window.addEventListener('mouseup', stopSegmentResize);
};

const getAiAssistBounds = () => {
  const fabSize = 64;
  const margin = 18;
  const viewportWidth = window.innerWidth || 1280;
  const viewportHeight = window.innerHeight || 720;
  const maxX = Math.max(margin, viewportWidth - fabSize - margin);
  const maxY = Math.max(margin, viewportHeight - fabSize - margin);
  return {
    margin,
    fabSize,
    viewportWidth,
    viewportHeight,
    minX: margin,
    minY: margin,
    maxX,
    maxY
  };
};

const getAiAssistPanelBounds = () => {
  const viewportWidth = window.innerWidth || 1280;
  const viewportHeight = window.innerHeight || 720;
  return {
    minWidth: 300,
    maxWidth: Math.max(300, Math.min(520, viewportWidth - 120)),
    minHeight: 320,
    maxHeight: Math.max(320, Math.min(760, viewportHeight - 120))
  };
};

const clampAiAssistPosition = () => {
  const bounds = getAiAssistBounds();
  aiAssistPosition.x = Math.min(Math.max(aiAssistPosition.x, bounds.minX), bounds.maxX);
  aiAssistPosition.y = Math.min(Math.max(aiAssistPosition.y, bounds.minY), bounds.maxY);
};

const clampAiAssistPanelSize = () => {
  const bounds = getAiAssistPanelBounds();
  aiAssistPanelSize.width = Math.min(Math.max(aiAssistPanelSize.width, bounds.minWidth), bounds.maxWidth);
  aiAssistPanelSize.height = Math.min(Math.max(aiAssistPanelSize.height, bounds.minHeight), bounds.maxHeight);
};

const snapAiAssistToEdge = () => {
  const bounds = getAiAssistBounds();
  const currentCenterX = aiAssistPosition.x + bounds.fabSize / 2;
  const viewportCenterX = bounds.viewportWidth / 2;
  if (currentCenterX <= viewportCenterX) {
    aiAssistPosition.x = bounds.minX;
    aiAssistSnapSide.value = 'left';
  } else {
    aiAssistPosition.x = bounds.maxX;
    aiAssistSnapSide.value = 'right';
  }
  aiAssistPosition.y = Math.min(Math.max(aiAssistPosition.y, bounds.minY), bounds.maxY);
};

const syncAiAssistPositionOnResize = () => {
  if (isAiAssistDragging.value || isAiAssistResizing.value) return;
  clampAiAssistPanelSize();
  clampAiAssistPosition();
  snapAiAssistToEdge();
};

const stopAiAssistResize = () => {
  if (!isAiAssistResizing.value) return;
  isAiAssistResizing.value = false;
  document.body.style.userSelect = '';
  window.removeEventListener('mousemove', handleAiAssistResizeMove);
  window.removeEventListener('mouseup', stopAiAssistResize);
};

const handleAiAssistResizeMove = (event) => {
  if (!isAiAssistResizing.value) return;
  const deltaX = event.clientX - aiAssistResizeStart.mouseX;
  const deltaY = event.clientY - aiAssistResizeStart.mouseY;
  aiAssistPanelSize.width = aiAssistResizeStart.width + deltaX;
  aiAssistPanelSize.height = aiAssistResizeStart.height + deltaY;
  clampAiAssistPanelSize();
};

const stopAiAssistDrag = () => {
  if (!isAiAssistDragging.value) return;
  isAiAssistDragging.value = false;
  document.body.style.userSelect = '';
  window.removeEventListener('mousemove', handleAiAssistDragMove);
  window.removeEventListener('mouseup', stopAiAssistDrag);
  snapAiAssistToEdge();
  window.setTimeout(() => {
    aiAssistDragMoved.value = false;
  }, 0);
};

const handleAiAssistDragMove = (event) => {
  if (!isAiAssistDragging.value) return;
  const deltaX = event.clientX - aiAssistDragStart.mouseX;
  const deltaY = event.clientY - aiAssistDragStart.mouseY;
  if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
    aiAssistDragMoved.value = true;
  }
  aiAssistPosition.x = aiAssistDragStart.x + deltaX;
  aiAssistPosition.y = aiAssistDragStart.y + deltaY;
  clampAiAssistPosition();
};

const startAiAssistDrag = (event) => {
  aiAssistDragStart.mouseX = event.clientX;
  aiAssistDragStart.mouseY = event.clientY;
  aiAssistDragStart.x = aiAssistPosition.x;
  aiAssistDragStart.y = aiAssistPosition.y;
  aiAssistDragMoved.value = false;
  isAiAssistDragging.value = true;
  document.body.style.userSelect = 'none';
  window.addEventListener('mousemove', handleAiAssistDragMove);
  window.addEventListener('mouseup', stopAiAssistDrag);
};

const startAiAssistResize = (event) => {
  aiAssistResizeStart.mouseX = event.clientX;
  aiAssistResizeStart.mouseY = event.clientY;
  aiAssistResizeStart.width = aiAssistPanelSize.width;
  aiAssistResizeStart.height = aiAssistPanelSize.height;
  isAiAssistResizing.value = true;
  document.body.style.userSelect = 'none';
  window.addEventListener('mousemove', handleAiAssistResizeMove);
  window.addEventListener('mouseup', stopAiAssistResize);
};

const toggleAiAssistPanel = () => {
  if (aiAssistDragMoved.value) return;
  showAiAssistPanel.value = !showAiAssistPanel.value;
  if (showAiAssistPanel.value) {
    loadAiAssistHistory();
  }
};

const scrollChatToBottom = async () => {
  await nextTick();
  if (!chatBodyRef.value) return;
  const scrollToEnd = () => {
    if (!chatBodyRef.value) return;
    chatBodyRef.value.scrollTop = chatBodyRef.value.scrollHeight;
  };
  scrollToEnd();
  requestAnimationFrame(scrollToEnd);
  setTimeout(scrollToEnd, 0);
};

const scrollAiAssistToBottom = async () => {
  await nextTick();
  if (!aiAssistChatBodyRef.value) return;
  const scrollToEnd = () => {
    if (!aiAssistChatBodyRef.value) return;
    aiAssistChatBodyRef.value.scrollTop = aiAssistChatBodyRef.value.scrollHeight;
  };
  scrollToEnd();
  requestAnimationFrame(scrollToEnd);
  setTimeout(scrollToEnd, 0);
};

const scrollSegmentEpisodeTextareaToBottom = async (episodeNo) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return;
  await nextTick();
  const card = document.querySelector(`.segment-episode-card[data-episode-no="${targetEpisode}"]`);
  if (!card) return;
  const textarea = card.querySelector('.segment-episode-editor textarea');
  if (!textarea) return;
  textarea.scrollTop = textarea.scrollHeight;
};

const scrollSegmentEpisodeCardIntoView = async (episodeNo) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return;
  await nextTick();
  const container = segmentOutlinePaneRef.value;
  if (!container) return;
  const card = container.querySelector(`.segment-episode-card[data-episode-no="${targetEpisode}"]`);
  if (!card) return;
  card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
};

const scrollDialogueEpisodeTextareaToBottom = async (episodeNo) => {
  const targetEpisode = Number(episodeNo || 0);
  if (!targetEpisode) return;
  await nextTick();
  const container = dialogueScriptListRef.value;
  if (!container) return;
  const card = container.querySelector(`.dialogue-episode-card[data-episode-no="${targetEpisode}"]`);
  if (!card) return;
  const textarea = card.querySelector('.dialogue-script-editor textarea');
  if (!textarea) return;
  textarea.scrollTop = textarea.scrollHeight;
};

const resetSegmentPlanScrollBottom = async () => {
  await nextTick();
  const reset = () => {
    if (segmentPlanListRef.value) {
      segmentPlanListRef.value.scrollTop = segmentPlanListRef.value.scrollHeight;
    }
    if (segmentOutlinePaneRef.value) {
      segmentOutlinePaneRef.value.scrollTop = segmentOutlinePaneRef.value.scrollHeight;
    }
  };
  reset();
  requestAnimationFrame(reset);
};

const parseSseChunk = (line, onContent, onError, onMeta) => {
  if (!line || !line.trim()) return;
  if (!line.startsWith('data: ')) return;
  const data = line.substring(6);
  if (!data || data === '[DONE]') return;
  let parsed;
  try {
    parsed = JSON.parse(data);
  } catch (error) {
    onContent(data);
    return;
  }
  if (parsed.heartbeat) return;
  if (parsed.error) {
    onError(parsed.error);
    return;
  }
  if (typeof onMeta === 'function') {
    onMeta(parsed);
  }
  if (parsed.fallback === true) {
    return;
  }
  if (typeof parsed.content === 'string' && parsed.content) {
    onContent(parsed.content);
  }
};

marked.setOptions({
  gfm: true,
  breaks: true
});

const normalizeRenderedMessageText = (raw) => {
  if (raw == null) return '';
  let text = String(raw);
  // Remove model-emitted thinking wrappers if they accidentally appear in visible output.
  text = text
    .replace(/<thinking\b[^>]*>[\s\S]*?<\/thinking>/gi, '')
    .replace(/&lt;thinking\b[^&]*&gt;[\s\S]*?&lt;\/thinking&gt;/gi, '')
    .replace(/<\/?thinking\b[^>]*>/gi, '')
    .replace(/&lt;\/?thinking\b[^&]*&gt;/gi, '');
  // Some model replies may contain escaped control chars in plain text/JSON-like output.
  // Normalize them before markdown rendering so \n becomes a real line break.
  text = text
    .replace(/\r\n/g, '\n')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '  ');
  return text.trim();
};

const renderMessageContent = (raw) => {
  if (!raw) return '';
  const normalized = normalizeRenderedMessageText(raw);
  const rendered = marked.parse(normalized);
  return DOMPurify.sanitize(
    typeof rendered === 'string' ? rendered : String(rendered),
    { USE_PROFILES: { html: true } }
  );
};

const stripMarkdownSyntax = (text) => {
  if (typeof text !== 'string') return text == null ? '' : String(text);
  let output = text.replace(/\r\n/g, '\n');
  output = output.replace(/^\s{0,3}#{1,6}\s+/gm, '');
  output = output.replace(/^\s{0,3}>\s?/gm, '');
  output = output.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');
  output = output.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  output = output.replace(/^\s*[-*+•]\s+\[[ xX]\]\s+/gm, '');
  output = output.replace(/^\s*[-*+•]\s+/gm, '');
  output = output.replace(/^\s*\d+[.)]\s+/gm, '');
  output = output.replace(/`{3,}/g, '');
  output = output.replace(/(`+)(.*?)\1/g, '$2');
  output = output.replace(/(\*\*|__)(.*?)\1/g, '$2');
  output = output.replace(/(\*|_)(.*?)\1/g, '$2');
  output = output.replace(/~~(.*?)~~/g, '$1');
  output = output.replace(/^[-*_]{3,}\s*$/gm, '');
  output = output.replace(/[ \t]+$/gm, '');
  return output;
};

const handleExportSegmentOutlineDocx = async () => {
  const exportEpisodes = visibleSegmentEpisodes.value.filter((episode) => (
    String(segmentEpisodeTexts.value[episode] || '').trim() !== ''
  ));
  if (!exportEpisodes.length) {
    ElMessage.warning('暂无可导出的分集大纲内容');
    return;
  }
  try {
    const children = [
      new Paragraph({
        text: `${project.value.title || defaultTitle.value} - 分集大纲`,
        heading: HeadingLevel.TITLE
      }),
      new Paragraph({ text: '' })
    ];
    exportEpisodes.forEach((episode) => {
      const segment = findSegmentByEpisode(episode);
      const segmentTitle = String(segment?.title || '');
      const segmentRange = segment
        ? `第${Number(segment.startEpisode || 0)}-${Number(segment.endEpisode || 0)}集`
        : '';
      const outlineText = stripMarkdownSyntax(String(segmentEpisodeTexts.value[episode] || ''));
      children.push(
        new Paragraph({
          text: `第${episode}集`,
          heading: HeadingLevel.HEADING_1
        })
      );
      if (segmentTitle || segmentRange) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `所属段落：${segmentTitle || '未命名段落'}${segmentRange ? `（${segmentRange}）` : ''}`
              })
            ]
          })
        );
      }
      const lines = outlineText.split(/\r?\n/);
      if (!lines.length || !outlineText.trim()) {
        children.push(new Paragraph({ text: '（暂无内容）' }));
      } else {
        lines.forEach((line) => {
          children.push(
            new Paragraph({
              children: [new TextRun(line || ' ')]
            })
          );
        });
      }
      children.push(new Paragraph({ text: '' }));
    });
    const doc = new Document({
      sections: [{ children }]
    });
    const blob = await Packer.toBlob(doc);
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `${project.value.title || defaultTitle.value}_分集大纲_${new Date().toISOString().slice(0, 10)}.docx`;
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    ElMessage.success('分集大纲导出成功');
  } catch (error) {
    ElMessage.error('分集大纲导出失败，请稍后重试');
  }
};

const handleExportDialogueDocx = async () => {
  const availableEpisodes = dialogueEpisodeList.value.filter((episode) => String(dialogueEpisodeTexts.value[episode] || '').trim() !== '');
  if (!availableEpisodes.length) {
    ElMessage.warning('暂无可导出的对话稿内容');
    return;
  }
  try {
    const children = [
      new Paragraph({
        text: `${project.value.title || defaultTitle.value} - 对话稿`,
        heading: HeadingLevel.TITLE
      }),
      new Paragraph({ text: '' })
    ];
    availableEpisodes.forEach((episode) => {
      const rawContent = dialogueEpisodeTexts.value[episode] || '';
      const content = stripMarkdownSyntax(rawContent);
      const lines = content.split(/\r?\n/);
      if (!lines.length || !content.trim()) {
        children.push(new Paragraph({ text: '（暂无内容）' }));
      } else {
        lines.forEach((line) => {
          children.push(
            new Paragraph({
              children: [new TextRun(line || ' ')]
            })
          );
        });
      }
      children.push(new Paragraph({ text: '' }));
    });
    const doc = new Document({
      sections: [{ children }]
    });
    const blob = await Packer.toBlob(doc);
    const fileName = `${project.value.title || '对话稿'}_${new Date().toISOString().slice(0, 10)}.docx`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    ElMessage.success('对话稿导出成功');
  } catch (error) {
    ElMessage.error('导出失败，请稍后重试');
  }
};

const refreshBalance = async () => {
  try {
    const response = await request.post('/user/refresh_user_data');
    if (!response || !Object.prototype.hasOwnProperty.call(response, 'balance_cny')) {
      return;
    }
    const nextBalance = Number(response.balance_cny || 0);
    const userInfoRaw = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');
    if (userInfoRaw) {
      const userInfo = JSON.parse(userInfoRaw);
      userInfo.balance_cny = nextBalance;
      if (localStorage.getItem('userInfo')) {
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
      }
      if (sessionStorage.getItem('userInfo')) {
        sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
      }
    }
    window.dispatchEvent(new CustomEvent('balanceUpdated', {
      detail: { balance_cny: nextBalance }
    }));
  } catch (error) {
    // 静默失败，不影响聊天主流程
  }
};

const streamAssistantReply = async (url, payload) => {
  isChatStreaming.value = true;
  const assistantMessage = reactive({ role: 'assistant', content: '', loading: true });
  let rollbackInput = '';
  let shouldRollbackInput = false;
  let requestRecoveredAfterFallback = false;
  let requestFailed = false;
  const maxRetries = 2;
  chatMessages.value.push(assistantMessage);
  await scrollChatToBottom();
  try {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const response = await request.streamRequest(url, payload, { timeout: 600000, silent: true });
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('流式通道不可用');
      }
      const decoder = new TextDecoder();
      let buffer = '';
      let streamFailed = false;
      let fallbackTriggered = false;
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            parseSseChunk(
              line,
              (content) => {
                assistantMessage.content += content;
              },
              (errorMessage) => {
                throw new Error(errorMessage || '模型返回异常');
              },
              (meta) => {
                if (meta && meta.fallback === true) {
                  shouldRollbackInput = true;
                  rollbackInput = typeof meta.rollback_input === 'string' ? meta.rollback_input : '';
                  fallbackTriggered = true;
                  throw new Error('__chat_fallback_retry__');
                }
              }
            );
          }
          await scrollChatToBottom();
        }
      } catch (error) {
        streamFailed = true;
        const isFallbackRetry = fallbackTriggered || error?.message === '__chat_fallback_retry__';
        if (isFallbackRetry) {
          assistantMessage.content = '';
        }
        const canRetry = attempt < maxRetries && (!assistantMessage.content.trim() || isFallbackRetry);
        if (!canRetry) {
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, 800 * (attempt + 1)));
      } finally {
        if (streamFailed && typeof reader.cancel === 'function') {
          try {
            await reader.cancel();
          } catch (cancelError) {
            // ignore
          }
        }
      }
      if (!streamFailed) {
        if (shouldRollbackInput) {
          requestRecoveredAfterFallback = true;
        }
        break;
      }
    }
    if (requestRecoveredAfterFallback) {
      shouldRollbackInput = false;
      rollbackInput = '';
    }
    if (!assistantMessage.content.trim()) {
      chatMessages.value = chatMessages.value.filter((item) => item !== assistantMessage);
      requestFailed = true;
    }
  } catch (error) {
    requestFailed = true;
    chatMessages.value = chatMessages.value.filter((item) => item !== assistantMessage);
  } finally {
    assistantMessage.loading = false;
    isChatStreaming.value = false;
    await scrollChatToBottom();
    await refreshBalance();
  }
  return {
    success: !requestFailed,
    shouldRollbackInput,
    rollbackInput
  };
};

const loadAiAssistHistory = async () => {
  if (!showAiAssistPanel.value) return;
  if (!isAiAssistAvailable.value) {
    clearAiAssistData();
    return;
  }
  const projectId = Number(project.value.id);
  if (!projectId || isAiAssistLoading.value) return;
  isAiAssistLoading.value = true;
  try {
    const data = await request.post('/slowDrama/ai_assist_history', {
      project_id: projectId,
      scene: currentAiAssistScene.value,
      context_mode: 'reply_help'
    });
    const messages = Array.isArray(data?.messages) ? data.messages : [];
    aiAssistMessages.value = messages
      .filter((item) => item && (item.role === 'assistant' || item.role === 'user'))
      .map((item) => ({
        role: item.role,
        content: item.content || '',
        loading: false
      }));
    await scrollAiAssistToBottom();
  } catch (error) {
    aiAssistMessages.value = [
      { role: 'assistant', content: 'AI辅助历史加载失败，请稍后重试。', loading: false }
    ];
  } finally {
    isAiAssistLoading.value = false;
  }
};

const streamAiAssistReply = async (payload) => {
  isAiAssistStreaming.value = true;
  const assistantMessage = reactive({ role: 'assistant', content: '', loading: true });
  let requestFailed = false;
  aiAssistMessages.value.push(assistantMessage);
  await scrollAiAssistToBottom();
  try {
    const response = await request.streamRequest('/slowDrama/ai_assist_chat_stream', payload, {
      timeout: 600000,
      silent: true
    });
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('流式通道不可用');
    }
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          parseSseChunk(
            line,
            (content) => {
              assistantMessage.content += content;
            },
            (errorMessage) => {
              throw new Error(errorMessage || 'AI辅助返回异常');
            }
          );
        }
        await scrollAiAssistToBottom();
      }
    } finally {
      if (typeof reader.cancel === 'function') {
        try {
          await reader.cancel();
        } catch (error) {
          // ignore
        }
      }
    }
    if (!assistantMessage.content.trim()) {
      aiAssistMessages.value = aiAssistMessages.value.filter((item) => item !== assistantMessage);
      requestFailed = true;
    }
  } catch (error) {
    requestFailed = true;
    aiAssistMessages.value = aiAssistMessages.value.filter((item) => item !== assistantMessage);
    ElMessage.warning(error?.message || 'AI辅助请求失败，请稍后重试');
  } finally {
    assistantMessage.loading = false;
    isAiAssistStreaming.value = false;
    await scrollAiAssistToBottom();
    await refreshBalance();
  }
  return !requestFailed;
};

const sendAiAssistMessage = async () => {
  if (!isAiAssistAvailable.value || isAiAssistStreaming.value) return;
  const text = aiAssistDraft.value.trim();
  if (!text) return;
  const pendingUserMessage = { role: 'user', content: text, loading: false };
  aiAssistMessages.value.push(pendingUserMessage);
  aiAssistDraft.value = '';
  await scrollAiAssistToBottom();
  const success = await streamAiAssistReply({
    project_id: Number(project.value.id),
    message: text,
    scene: currentAiAssistScene.value,
    context_mode: 'reply_help'
  });
  if (!success) {
    aiAssistMessages.value = aiAssistMessages.value.filter((item) => item !== pendingUserMessage);
    aiAssistDraft.value = text;
  }
};

const initChat = async () => {
  const projectId = Number(project.value.id);
  if (!projectId || isInitializingChat.value) return;
  isInitializingChat.value = true;
  const scene = currentChatScene.value;
  try {
    const data = await request.post('/slowDrama/chat_history', {
      project_id: projectId,
      scene
    });
    const messages = Array.isArray(data?.messages) ? data.messages : [];
    chatMessages.value = messages
      .filter((item) => item && (item.role === 'assistant' || item.role === 'user'))
      .map((item) => ({
        role: item.role,
        content: item.content || '',
        loading: false
      }));
    await scrollChatToBottom();
    if (chatMessages.value.length === 0) {
      await streamAssistantReply('/slowDrama/init_chat_stream', {
        project_id: projectId,
        scene
      });
    }
  } catch (error) {
    chatMessages.value = [
      { role: 'assistant', content: '聊天历史加载失败，请刷新后重试。', loading: false }
    ];
  } finally {
    isInitializingChat.value = false;
  }
};

const sendMessage = async () => {
  const text = chatInput.value.trim();
  if (!text || isChatStreaming.value) return;
  const pendingUserMessage = { role: 'user', content: text, loading: false };
  chatMessages.value.push(pendingUserMessage);
  await scrollChatToBottom();
  chatInput.value = '';
  const streamResult = await streamAssistantReply('/slowDrama/chat_stream', {
    project_id: Number(project.value.id),
    message: text,
    scene: currentChatScene.value
  });
  if (streamResult?.success === false || streamResult?.shouldRollbackInput) {
    chatMessages.value = chatMessages.value.filter((item) => item !== pendingUserMessage);
    chatInput.value = streamResult.rollbackInput || text;
  }
};

const addThemeTag = () => {
  const value = themeDraft.value.trim();
  if (!value) return;
  if (projectMeta.value.theme.length >= 10) {
    ElMessage.warning('最多 10 个标签');
    themeDraft.value = '';
    return;
  }
  if (!projectMeta.value.theme.includes(value)) {
    projectMeta.value.theme.push(value);
  }
  themeDraft.value = '';
};

const removeThemeTag = (tag) => {
  projectMeta.value.theme = projectMeta.value.theme.filter((item) => item !== tag);
};

const focusThemeInput = () => {
  themeInputRef.value?.focus();
};

const normalizeThemeTags = (tags) => {
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string' && tags.trim()) {
    try {
      const parsed = JSON.parse(tags);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
  return [];
};

const initDetailData = async () => {
  const projectId = Number(project.value.id);
  if (!projectId) {
    ElMessage.error('项目ID无效');
    return;
  }
  try {
    const data = await request.post('/slowDrama/init_detail', {
      project_id: projectId,
      workflow_type: workflowType.value
    });
    project.value = {
      ...project.value,
      id: data.project_id,
      title: data.title || project.value.title
    };
    persistProjectCache(data.project_id, project.value.title);
    projectMeta.value.coreAudience = data.core_audience || '';
    projectMeta.value.theme = normalizeThemeTags(data.theme_tags).slice(0, 10);
    seedReportText.value = data.seed_report || data.story_synopsis || data.character_bio || '';
    savedSeedReport.value = seedReportText.value;
  } catch (error) {
    ElMessage.error('初始化详情失败');
  }
};

const saveDetail = async () => {
  const projectId = Number(project.value.id);
  if (!projectId || isSavingDetail.value) return false;
  isSavingDetail.value = true;
  try {
    const payload = {
      project_id: projectId,
      core_audience: projectMeta.value.coreAudience || '',
      theme_tags: JSON.stringify(projectMeta.value.theme || []),
      seed_report: seedReportText.value || ''
    };
    const data = await request.post('/slowDrama/save_detail', payload);
    projectMeta.value.coreAudience = data.core_audience || '';
    projectMeta.value.theme = normalizeThemeTags(data.theme_tags).slice(0, 10);
    seedReportText.value = data.seed_report || data.story_synopsis || data.character_bio || '';
    savedSeedReport.value = seedReportText.value;
    ElMessage.success('保存成功');
    return true;
  } catch (error) {
    ElMessage.error('保存失败，请稍后重试');
    return false;
  } finally {
    isSavingDetail.value = false;
  }
};

const getCurrentSectionLoadKey = () => {
  const routeProjectId = route.params.id || route.query.id || project.value.id || '';
  return `${String(route.name || '')}|${String(routeProjectId)}|${String(activeSection.value || '')}`;
};

const loadSectionData = async () => {
  const loadKey = getCurrentSectionLoadKey();
  if (activeSectionLoadPromise && activeSectionLoadKey === loadKey) {
    return activeSectionLoadPromise;
  }
  activeSectionLoadKey = loadKey;
  activeSectionLoadPromise = (async () => {
  if (activeSection.value !== 'dialogue_script') {
    clearChatPanelData();
  }
  isSectionLoading.value = true;
  try {
    await initDetailData();
    if (activeSection.value === 'segment_plan' || activeSection.value === 'dialogue_script') {
      await loadSegmentPlanData();
    }
    if (activeSection.value === 'dialogue_script') {
      await loadDialogueScriptData();
    }
  } finally {
    isSectionLoading.value = false;
  }
  if (activeSection.value === 'dialogue_script') {
    // Default generation range: 1 ~ latest outline episode (fallback to configured max).
    const latestOutlineEpisode = getLatestEpisodeNoFromTextMap(segmentEpisodeTexts.value) || Number(dialogueDisplayEpisodeRange.value?.[1] || 0);
    dialogueStartEpisode.value = 1;
    dialogueEndEpisode.value = Math.max(1, latestOutlineEpisode);

    // Scroll both panes to latest generated dialogue episode; fallback to latest outline.
    const latestDialogueEpisode = getLatestEpisodeNoFromTextMap(dialogueEpisodeTexts.value);
    const targetEpisode = latestDialogueEpisode || latestOutlineEpisode;
    if (targetEpisode) {
      await scrollDialogueListsToEpisode(targetEpisode);
    }
  }
  if (activeSection.value === 'segment_plan') {
    await resetSegmentPlanScrollBottom();
  }
  if (activeSection.value !== 'dialogue_script') {
    await initChat();
    await scrollChatToBottom();
  }
  })();
  try {
    await activeSectionLoadPromise;
  } finally {
    if (activeSectionLoadKey === loadKey) {
      activeSectionLoadPromise = null;
      activeSectionLoadKey = '';
    }
  }
};

onBeforeUnmount(() => {
  stopResize();
  stopSegmentResize();
  stopAiAssistDrag();
  stopAiAssistResize();
  window.removeEventListener('resize', syncAiAssistPositionOnResize);
});

onMounted(async () => {
  const bounds = getAiAssistBounds();
  aiAssistPosition.x = bounds.maxX;
  aiAssistPosition.y = Math.min(Math.max(144, bounds.minY), bounds.maxY);
  aiAssistSnapSide.value = 'right';
  clampAiAssistPanelSize();
  window.addEventListener('resize', syncAiAssistPositionOnResize);
  if (!isCurrentWorkflowDetailRoute.value) return;
  syncProjectFromRoute();
  activeSection.value = 'synopsis';
  await loadSectionData();
});

onActivated(async () => {
  if (!isCurrentWorkflowDetailRoute.value) return;
  if (skipFirstActivatedLoad.value) {
    skipFirstActivatedLoad.value = false;
    return;
  }
  syncProjectFromRoute();
  activeSection.value = 'synopsis';
  await loadSectionData();
  await scrollChatToBottom();
  setTimeout(() => {
    if (!chatBodyRef.value) return;
    chatBodyRef.value.scrollTop = chatBodyRef.value.scrollHeight;
  }, 120);
});

watch(
  () => chatMessages.value.length,
  async () => {
    await scrollChatToBottom();
  }
);

watch(
  () => aiAssistMessages.value.length,
  async () => {
    await scrollAiAssistToBottom();
  }
);

watch(
  () => [route.params.id, route.query.id, route.query.title],
  async () => {
    if (!isCurrentWorkflowDetailRoute.value) return;
    const changed = syncProjectFromRoute();
    if (!changed) return;
    activeSection.value = 'synopsis';
    await loadSectionData();
  }
);

watch(
  () => [showAiAssistPanel.value, activeSection.value, project.value.id],
  async ([visible]) => {
    if (!visible) return;
    await loadAiAssistHistory();
  }
);

watch(
  () => dialogueDisplayEpisodeRange.value,
  ([minEpisode, maxEpisode]) => {
    if (!minEpisode || !maxEpisode || minEpisode > maxEpisode) return;
    const currentStart = Number(dialogueStartEpisode.value || 0);
    const currentEnd = Number(dialogueEndEpisode.value || 0);
    const nextStart = Math.min(Math.max(currentStart || minEpisode, minEpisode), maxEpisode);
    const nextEnd = Math.min(Math.max(currentEnd || maxEpisode, minEpisode), maxEpisode);
    dialogueStartEpisode.value = nextStart;
    dialogueEndEpisode.value = Math.max(nextStart, nextEnd);
  },
  { immediate: true }
);

</script>

<style scoped>
.slow-drama-detail {
  height: calc(100vh - 132px);
  min-height: 620px;
  display: grid;
  grid-template-columns: clamp(220px, var(--left-panel-width, 280px), 34vw) minmax(0, 1fr) 200px;
  gap: 16px;
  padding: 4px 0 8px;
  background: #f7f9fc;
}

.slow-drama-detail.no-chat-layout {
  grid-template-columns: minmax(0, 1fr) 200px;
}

.ai-assist-floating-layer {
  position: fixed;
  z-index: 4000;
  pointer-events: none;
}

.ai-assist-fab,
.ai-assist-panel {
  pointer-events: auto;
}

.ai-assist-fab {
  width: 58px;
  height: 58px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
  cursor: grab;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.ai-assist-fab:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.16);
}

.ai-assist-fab.dragging {
  cursor: grabbing;
  transform: scale(1.03);
  transition: none;
}

.ai-assist-fab-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4b5563 0%, #6d28d9 100%);
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 1px;
}

.ai-assist-panel {
  position: absolute;
  top: 0;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 22px 56px rgba(15, 23, 42, 0.16);
  backdrop-filter: blur(8px);
}

.ai-assist-panel.align-right {
  right: 76px;
}

.ai-assist-panel.align-left {
  left: 76px;
}

.ai-assist-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 15px 16px 13px;
  background: #fff;
  color: #111827;
  border-bottom: 1px solid #edf2f7;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.8);
}

.ai-assist-header-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.ai-assist-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #374151 0%, #7c3aed 100%);
  box-shadow: 0 8px 16px rgba(124, 58, 237, 0.18);
}

.ai-assist-header-meta {
  min-width: 0;
}

.ai-assist-panel-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  letter-spacing: 0.2px;
}

.ai-assist-panel-subtitle {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
}

.ai-assist-close-btn {
  min-width: 60px;
  height: 32px;
  padding: 0 12px;
  border-radius: 10px;
  border-color: #ddd6fe;
  background: #f5f3ff;
  color: #6d28d9;
  font-weight: 600;
}

.ai-assist-close-btn:hover {
  border-color: #c4b5fd;
  background: #ede9fe;
  color: #5b21b6;
}

.ai-assist-panel-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 12px;
  padding: 14px 16px 16px;
  background: #f8fafc;
}

.ai-assist-chat-preview {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px 2px 2px 0;
}

.ai-assist-preview-row {
  display: flex;
}

.ai-assist-preview-row.user {
  justify-content: flex-end;
}

.ai-assist-preview-bubble {
  max-width: 88%;
  padding: 9px 11px;
  border-radius: 14px;
  font-size: 12px;
  line-height: 1.65;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.ai-assist-preview-bubble.loading {
  opacity: 0.9;
}

.ai-assist-rich-content {
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.ai-assist-bubble-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
}

.ai-assist-bubble-loading.inline {
  margin-top: 8px;
}

.ai-assist-bubble-loading-text {
  font-size: 12px;
}

.ai-assist-bubble-loading-dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.ai-assist-bubble-loading-dots i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.26;
  animation: ai-assist-dot-pulse 1.1s ease-in-out infinite;
}

.ai-assist-bubble-loading-dots i:nth-child(2) {
  animation-delay: 0.18s;
}

.ai-assist-bubble-loading-dots i:nth-child(3) {
  animation-delay: 0.36s;
}

@keyframes ai-assist-dot-pulse {
  0%,
  80%,
  100% {
    opacity: 0.22;
    transform: scale(0.85);
  }

  40% {
    opacity: 0.9;
    transform: scale(1);
  }
}

.ai-assist-preview-bubble :deep(p) {
  margin: 0;
}

.ai-assist-preview-bubble :deep(p + p) {
  margin-top: 8px;
}

.ai-assist-rich-content :deep(h1),
.ai-assist-rich-content :deep(h2),
.ai-assist-rich-content :deep(h3),
.ai-assist-rich-content :deep(p),
.ai-assist-rich-content :deep(ul),
.ai-assist-rich-content :deep(ol),
.ai-assist-rich-content :deep(pre),
.ai-assist-rich-content :deep(blockquote) {
  margin: 0 0 8px;
}

.ai-assist-rich-content :deep(h1:last-child),
.ai-assist-rich-content :deep(h2:last-child),
.ai-assist-rich-content :deep(h3:last-child),
.ai-assist-rich-content :deep(p:last-child),
.ai-assist-rich-content :deep(ul:last-child),
.ai-assist-rich-content :deep(ol:last-child),
.ai-assist-rich-content :deep(pre:last-child),
.ai-assist-rich-content :deep(blockquote:last-child) {
  margin-bottom: 0;
}

.ai-assist-rich-content :deep(ul),
.ai-assist-rich-content :deep(ol) {
  padding-left: 18px;
}

.ai-assist-rich-content :deep(li) {
  margin: 4px 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.ai-assist-rich-content :deep(code) {
  padding: 1px 4px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
  font-family: Consolas, Monaco, monospace;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-wrap;
}

.ai-assist-rich-content :deep(pre) {
  max-width: 100%;
  overflow-x: hidden;
  border-radius: 8px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.05);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.ai-assist-rich-content :deep(pre code) {
  padding: 0;
  background: transparent;
  white-space: pre-wrap;
}

.ai-assist-rich-content :deep(table) {
  display: table;
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.ai-assist-rich-content :deep(th),
.ai-assist-rich-content :deep(td) {
  min-width: 0;
  padding: 6px 8px;
  vertical-align: top;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
}

.ai-assist-preview-row.assistant .ai-assist-preview-bubble {
  background: #fff;
  color: #4b5563;
  border: 1px solid #e8edf3;
  border-top-left-radius: 6px;
}

.ai-assist-preview-row.user .ai-assist-preview-bubble {
  background: #ede9fe;
  color: #4c1d95;
  border-top-right-radius: 6px;
}

.ai-assist-empty-state {
  margin: auto 0;
  padding: 14px 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px dashed #d8dee9;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.7;
}

.ai-assist-input-card {
  flex-shrink: 0;
  padding: 12px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e9eef5;
}

.ai-assist-footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
}

.ai-assist-resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 22px;
  height: 22px;
  cursor: nwse-resize;
}

.ai-assist-resize-handle::before {
  content: '';
  position: absolute;
  right: 6px;
  bottom: 6px;
  width: 10px;
  height: 10px;
  border-right: 2px solid #c4b5fd;
  border-bottom: 2px solid #c4b5fd;
  border-bottom-right-radius: 2px;
}

.ai-assist-demo-list {
  margin: 0;
  padding-left: 18px;
  color: #5a647f;
  font-size: 13px;
  line-height: 1.8;
}

.ai-assist-panel-fade-enter-active,
.ai-assist-panel-fade-leave-active {
  transition: all 0.18s ease;
}

.ai-assist-panel-fade-enter-from,
.ai-assist-panel-fade-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.96);
}

.left-chat-panel,
.center-panel,
.right-nav-panel {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(16, 24, 40, 0.04);
  min-width: 0;
}

.left-chat-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.left-header {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #7d6cf0 100%);
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: .5px;
}

.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  padding-right: 10px;
  background: linear-gradient(180deg, #f8faff 0%, #f4f7ff 100%);
  scrollbar-gutter: stable;
}

.msg-row {
  display: flex;
  margin-bottom: 10px;
}

.msg-row.user {
  justify-content: flex-end;
}

.msg-bubble {
  max-width: 88%;
  overflow: hidden;
  border-radius: 12px;
  padding: 0px 12px;
  line-height: 1.5;
  font-size: 13px;
  color: #1f2937;
  border: 1px solid #e5e7eb;
  background: #fff;
  white-space: pre-wrap;
}

.msg-content {
  white-space: normal;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.msg-content p,
.msg-content h1,
.msg-content h2,
.msg-content h3,
.msg-content ul,
.msg-content pre {
  margin: 0 0 8px;
}

.msg-content p:last-child,
.msg-content h1:last-child,
.msg-content h2:last-child,
.msg-content h3:last-child,
.msg-content ul:last-child,
.msg-content pre:last-child {
  margin-bottom: 0;
}

.msg-content h1,
.msg-content h2,
.msg-content h3 {
  font-size: 14px;
  font-weight: 700;
}

.msg-content ul {
  padding-left: 18px;
}

.msg-content li {
  margin: 4px 0;
}

.msg-content code {
  padding: 1px 4px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
  font-family: Consolas, Monaco, monospace;
}

.msg-content pre {
  overflow-x: auto;
  border-radius: 8px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.05);
}

.msg-content table {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  table-layout: fixed;
}

.msg-content th,
.msg-content td {
  min-width: 0;
  padding: 6px 8px;
  vertical-align: top;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
}

.msg-content pre code {
  padding: 0;
  background: transparent;
}

.msg-bubble.loading {
  border-color: #d7def7;
}

.typing-dots {
  display: inline-flex;
  gap: 4px;
  margin-left: 8px;
  vertical-align: middle;
}

.typing-dots i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #8a96c4;
  animation: typingBlink 1.2s infinite ease-in-out;
}

.typing-dots i:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dots i:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typingBlink {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-1px);
  }
}

.msg-row.user .msg-bubble {
  background: #667eea;
  border-color: #667eea;
  color: #fff;
}

.chat-input-wrap {
  border-top: 1px solid #edf1f7;
  padding: 12px;
  background: #fff;
}

.composer-shell {
  border: 1px solid #dce4f5;
  border-radius: 14px;
  background: linear-gradient(180deg, #fbfdff 0%, #f6f9ff 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75);
  overflow: hidden;
}

:deep(.composer-input .el-textarea__inner) {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  min-height: 110px !important;
  padding: 14px;
  line-height: 1.6;
  font-size: 13px;
}

.composer-footer {
  height: 42px;
  border-top: 1px solid #e6ecf8;
  background: rgba(243, 247, 255, 0.85);
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.send-btn {
  min-width: 74px;
  height: 30px;
  border-radius: 9px;
}

.resize-handle {
  position: absolute;
  top: 0;
  right: -12px;
  width: 24px;
  height: 100%;
  cursor: col-resize;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.resize-handle::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 5px;
  height: 86px;
  border-radius: 999px;
  background: linear-gradient(180deg, #8ea1ff 0%, #6f82f8 100%);
  box-shadow: 0 0 0 1px rgba(111, 130, 248, 0.15);
  opacity: 0.95;
  transition: all .2s ease;
}

.resize-handle::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 120px;
  border-radius: 999px;
  background: rgba(111, 130, 248, 0.08);
}

.resize-hint {
  position: absolute;
  top: 12px;
  right: -36px;
  font-size: 11px;
  color: #5f70ff;
  background: #eef2ff;
  border: 1px solid #d9e1ff;
  border-radius: 999px;
  padding: 2px 8px;
  line-height: 1.4;
  font-weight: 600;
  pointer-events: none;
}

.left-chat-panel:hover .resize-handle::before {
  width: 6px;
  height: 96px;
  background: linear-gradient(180deg, #6f82f8 0%, #586cf0 100%);
}

.left-chat-panel:hover .resize-handle::after {
  background: rgba(111, 130, 248, 0.14);
}

.center-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.center-header {
  height: 68px;
  border-bottom: 1px solid #edf1f7;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
}

.back-btn {
  border-radius: 8px;
}

.center-title {
  flex: 1;
  text-align: center;
}

.center-title h2 {
  margin: 0;
  color: #5f70ff;
  font-size: 22px;
  font-weight: 700;
}

.center-title p {
  margin: 2px 0 0;
  color: #6b7280;
  font-size: 13px;
}

.meta-strip {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding: 12px 14px 0;
}

.meta-card {
  border: 1px solid #ebeff6;
  border-radius: 10px;
  padding: 10px 12px;
  background: #f9fbff;
}

.meta-label {
  color: #6b7280;
  font-size: 12px;
}

.meta-value {
  margin-top: 4px;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
}

.tag-input-box {
  margin-top: 6px;
  border-radius: 8px;
  box-shadow: 0 0 0 1px #dde5f7 inset;
  background: #fff;
  min-height: 36px;
  padding: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  cursor: text;
}

.theme-tag {
  border-radius: 6px;
}

:deep(.meta-input .el-input__wrapper) {
  margin-top: 6px;
  border-radius: 8px;
  box-shadow: 0 0 0 1px #dde5f7 inset;
  background: #fff;
}

.tag-input {
  border: none;
  outline: none;
  background: transparent;
  min-width: 140px;
  flex: 1;
  height: 26px;
  font-size: 13px;
  color: #374151;
}

.tag-input::placeholder {
  color: #9aa4b2;
}

.tag-count {
  margin-top: 6px;
  text-align: right;
  font-size: 12px;
  color: #8b96a8;
}

.content-wrap {
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.content-section {
  height: 100%;
  overflow-y: auto;
  padding: 12px 14px 14px;
  padding-right: 10px;
  scrollbar-gutter: stable;
}

:deep(.section-editor .el-textarea__inner) {
  min-height: calc(100vh - 340px) !important;
  border: none;
  box-shadow: none;
  padding: 10px 6px;
  font-size: 16px;
  line-height: 1.85;
  color: #1f2937;
}

.synopsis-split {
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.synopsis-panel {
  border: 1px solid #e8edf5;
  border-radius: 12px;
  background: #fff;
  padding: 10px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.synopsis-title {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 8px;
}

:deep(.synopsis-editor .el-textarea__inner) {
  min-height: calc(100vh - 420px) !important;
  border: 1px solid #e7ecf6;
  box-shadow: none;
  border-radius: 10px;
  padding: 10px;
  font-size: 14px;
  line-height: 1.8;
}

.segment-plan-wrap {
  flex: 1;
  min-height: 0;
  padding: 14px;
  overflow-y: auto;
  overflow-x: hidden;
}

.dialogue-script-wrap {
  flex: 1;
  min-height: 0;
  padding: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dialogue-toolbar {
  border: 1px solid #edf1f7;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.dialogue-range-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.dialogue-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.dialogue-range-label,
.dialogue-range-sep {
  color: #64748b;
  font-size: 13px;
  white-space: nowrap;
}

.dialogue-ep-select {
  width: 112px;
}

.dialogue-batch-regenerate-dialog {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.dialogue-batch-regenerate-layout {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 16px;
  min-height: 0;
}

.dialogue-batch-regenerate-left,
.dialogue-batch-regenerate-right {
  min-width: 0;
}

.dialogue-batch-regenerate-panel-title {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #334155;
}

.dialogue-batch-regenerate-tip {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}

.dialogue-batch-regenerate-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  max-height: 360px;
  overflow-y: auto;
  padding: 2px 4px 2px 0;
}

.dialogue-batch-regenerate-item {
  border: 1px solid #e8edf5;
  border-radius: 12px;
  background: #fafcff;
  padding: 12px;
}

.dialogue-batch-regenerate-right {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.dialogue-batch-regenerate-item-title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.dialogue-batch-regenerate-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  border: 1px dashed #d8e1f2;
  border-radius: 12px;
  background: #f8fbff;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
  padding: 16px;
}

.dialogue-layout {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.dialogue-generate-progress-text {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 6px;
}

.dialogue-generate-inline {
  width: 100%;
  border: 1px solid #e7ecf6;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
}

.dialogue-pane {
  min-width: 0;
  min-height: 0;
  border: 1px solid #e7ecf6;
  border-radius: 12px;
  background: #f8fbff;
  padding: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dialogue-pane-title {
  font-size: 16px;
  font-weight: 700;
  color: #374151;
  padding: 2px 2px 10px;
  flex-shrink: 0;
}

.dialogue-episode-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  padding-bottom: 12px;
  scrollbar-gutter: stable;
}

.chat-body::-webkit-scrollbar,
.content-section::-webkit-scrollbar,
.segment-plan-list::-webkit-scrollbar,
.segment-right-pane::-webkit-scrollbar,
.dialogue-episode-list::-webkit-scrollbar {
  width: 7px;
  height: 7px;
}

.chat-body::-webkit-scrollbar-track,
.content-section::-webkit-scrollbar-track,
.segment-plan-list::-webkit-scrollbar-track,
.segment-right-pane::-webkit-scrollbar-track,
.dialogue-episode-list::-webkit-scrollbar-track {
  background: transparent;
}

.chat-body::-webkit-scrollbar-thumb,
.content-section::-webkit-scrollbar-thumb,
.segment-plan-list::-webkit-scrollbar-thumb,
.segment-right-pane::-webkit-scrollbar-thumb,
.dialogue-episode-list::-webkit-scrollbar-thumb {
  background: #d6dfef;
  border-radius: 999px;
}

.chat-body::-webkit-scrollbar-thumb:hover,
.content-section::-webkit-scrollbar-thumb:hover,
.segment-plan-list::-webkit-scrollbar-thumb:hover,
.segment-right-pane::-webkit-scrollbar-thumb:hover,
.dialogue-episode-list::-webkit-scrollbar-thumb:hover {
  background: #c5d2e8;
}

.dialogue-episode-card {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  background: #fff;
  flex-shrink: 0;
}

.dialogue-episode-title {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2fb;
  background: #f9fbff;
  border-radius: 12px 12px 0 0;
}

.dialogue-episode-title-text {
  flex: 0 0 auto;
}

.dialogue-episode-word-count {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  margin-left: auto;
}

.dialogue-episode-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

:deep(.dialogue-outline-editor .el-textarea__inner),
:deep(.dialogue-script-editor .el-textarea__inner) {
  overflow: hidden !important;
  border: none;
  box-shadow: none !important;
  border-radius: 0;
  padding: 12px;
  font-size: 16px;
  line-height: 1.75;
}

:deep(.dialogue-save-btn.el-button),
:deep(.dialogue-generate-btn.el-button) {
  min-width: 68px;
  height: 30px;
  border-radius: 999px;
  font-weight: 600;
  transition: all .18s ease;
}

:deep(.dialogue-save-btn.el-button) {
  color: #475569;
  border-color: #d8e1f2;
  background: #f8fbff;
}

:deep(.dialogue-save-btn.el-button:hover) {
  color: #334155;
  border-color: #c3d0ea;
  background: #eef4ff;
}

:deep(.dialogue-generate-btn.el-button) {
  border-color: #5b8cff;
  background: linear-gradient(135deg, #6f8dff, #4f7eff);
  color: #fff;
}

:deep(.dialogue-generate-btn.el-button:hover) {
  border-color: #4a79f8;
  background: linear-gradient(135deg, #6486ff, #4678fb);
}

.segment-plan-layout {
  height: 100%;
  display: flex;
  gap: 14px;
  min-height: 0;
  align-items: stretch;
}

.segment-left-pane {
  flex: 0 0 var(--segment-left-width, 46%);
  min-width: 280px;
  max-width: 72%;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-right: 4px;
}

.segment-plan-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-right: 10px;
  scrollbar-gutter: stable;
}

.segment-splitter {
  width: 6px;
  border-radius: 999px;
  background: #edf2fb;
  border: 1px solid #e6ebf6;
  cursor: col-resize;
  flex: 0 0 6px;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  transition: background-color .18s ease, border-color .18s ease;
}

.segment-splitter:hover {
  background: #dfe8fb;
  border-color: #d2def8;
}

.segment-splitter-hint {
  display: none;
}

.segment-right-pane {
  flex: 1;
  min-width: 0;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
  /* border: 1px solid #e7ecf6; */
  border-radius: 12px;
  /* background: #f8fbff; */
  padding: 10px 10px 10px 0px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.segment-right-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 700;
  color: #374151;
  flex-shrink: 0;
  gap: 12px;
}

.segment-export-btn {
  flex-shrink: 0;
}

.segment-right-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;
  scrollbar-gutter: stable;
}

.segment-episode-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.segment-empty-state {
  border: 1px dashed #d9e3f5;
  border-radius: 10px;
  background: #fff;
  color: #8a94a8;
  font-size: 13px;
  text-align: center;
  padding: 28px 14px;
}

.segment-episode-card {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  background: #fff;
  width: 100%;
}

.segment-episode-title {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2fb;
  background: #f9fbff;
  border-radius: 12px 12px 0 0;
  cursor: pointer;
  user-select: none;
  transition: border-radius 0.2s;
}
.segment-episode-title.collapsed {
  border-radius: 12px;
  border-bottom: none;
}

.segment-episode-collapse-arrow {
  flex: 0 0 auto;
  font-size: 10px;
  color: #94a3b8;
  transition: transform 0.2s;
}
.segment-episode-collapse-arrow:not(.collapsed) {
  transform: rotate(90deg);
}

.segment-episode-body {
  overflow: hidden;
}

/* 展开/收起动画 */
.episode-collapse-enter-active {
  transition: max-height 0.3s ease, opacity 0.25s ease;
  max-height: 3000px;
}
.episode-collapse-leave-active {
  transition: max-height 0.25s ease, opacity 0.2s ease;
  max-height: 3000px;
}
.episode-collapse-enter-from,
.episode-collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.segment-episode-title-text {
  flex: 0 0 auto;
}

.segment-episode-word-count {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  margin-left: auto;
}

.segment-episode-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

:deep(.segment-episode-editor .el-textarea__inner) {
  overflow: hidden !important;
  border: none;
  box-shadow: none !important;
  border-radius: 0;
  padding: 12px;
  font-size: 16px;
  line-height: 1.75;
}

.segment-episode-save-btn {
  flex-shrink: 0;
}

:deep(.segment-episode-save-btn.el-button),
:deep(.dialogue-script-save-btn.el-button) {
  min-width: 68px;
  height: 30px;
  border-radius: 999px;
  font-weight: 600;
  color: #3b82f6;
  border-color: #bfdbfe;
  background: #eff6ff;
  transition: all .18s ease;
}

:deep(.segment-episode-save-btn.el-button:hover),
:deep(.dialogue-script-save-btn.el-button:hover) {
  color: #2563eb;
  border-color: #93c5fd;
  background: #dbeafe;
}

:deep(.segment-episode-regenerate-btn.el-button),
:deep(.dialogue-script-regenerate-btn.el-button) {
  min-width: 78px;
  height: 30px;
  border-radius: 999px;
  font-weight: 600;
  color: #475569;
  border-color: #d8e1f2;
  background: #f8fbff;
  transition: all .18s ease;
}

:deep(.segment-episode-regenerate-btn.el-button:hover),
:deep(.dialogue-script-regenerate-btn.el-button:hover) {
  color: #334155;
  border-color: #c3d0ea;
  background: #eef4ff;
}

.segment-toolbar {
  border: 1px solid #edf1f7;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.segment-toolbar-info {
  min-width: 0;
}

.segment-toolbar-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  line-height: 1.2;
}

.segment-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.segment-plan-card {
  border: 1px solid #e9eef8;
  border-top: 3px solid #ff9f43;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  flex-shrink: 0;
}

.segment-plan-header {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  padding: 14px 16px;
  background: #f7f9ff;
  border-bottom: 1px solid #edf2fb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

:deep(.segment-plan-editor .el-textarea__inner) {
  min-height: 220px !important;
  border: none;
  box-shadow: none !important;
  border-radius: 0;
  padding: 16px;
  font-size: 16px;
  line-height: 1.8;
}

.segment-plan-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 14px 14px;
  border-top: 1px solid #edf2fb;
}

.segment-range-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  flex: 1 1 260px;
}

.segment-action-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  flex: 1 1 260px;
}

.segment-range-label,
.segment-range-sep {
  color: #6b7280;
  font-size: 14px;
}

.segment-ep-select {
  width: 120px;
}

.segment-generate-btn,
.segment-save-btn {
  margin-left: 0;
}

:deep(.segment-save-btn.el-button),
:deep(.segment-generate-btn.el-button) {
  min-width: 78px;
  height: 34px;
  border-radius: 10px;
  font-weight: 600;
  transition: all .18s ease;
}

:deep(.segment-save-btn.el-button) {
  color: #475569;
  border-color: #d8e1f2;
  background: #f8fbff;
}

:deep(.segment-save-btn.el-button:hover) {
  color: #334155;
  border-color: #c3d0ea;
  background: #eef4ff;
}

:deep(.segment-generate-btn.el-button) {
  border-color: #5b8cff;
  background: linear-gradient(135deg, #6f8dff, #4f7eff);
  color: #fff;
}

:deep(.segment-generate-btn.el-button:hover) {
  border-color: #4a79f8;
  background: linear-gradient(135deg, #6486ff, #4678fb);
}

.segment-generate-progress {
  margin-left: 0;
  color: #64748b;
  font-size: 13px;
}

@media (max-width: 1500px) {
  .segment-left-pane {
    min-width: 260px;
  }
}

@media (max-width: 1200px) {
  .segment-plan-layout {
    display: block;
  }

  .segment-left-pane,
  .segment-right-pane {
    width: 100%;
    max-width: none;
    max-height: none;
    flex: none;
  }

  .segment-right-pane {
    max-height: 46vh;
    margin-top: 12px;
  }

  .segment-splitter {
    display: none;
  }

  .segment-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .segment-toolbar-actions {
    width: 100%;
    justify-content: space-between;
  }
}

.right-nav-panel {
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: start;
  position: sticky;
  top: 12px;
}

.nav-item {
  font-size: 15px;
  font-weight: 600;
  border-radius: 10px;
  padding: 10px 10px;
  cursor: pointer;
  color: #374151;
  border: 1px solid transparent;
  transition: all .2s ease;
}

.nav-item:hover {
  background: #f7f9ff;
  border-color: #e4eaff;
}

.nav-item.active {
  background: #eef3ff;
  color: #5f70ff;
  border-color: #dbe5ff;
}

.nav-item.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@media (max-width: 1440px) {
  .slow-drama-detail {
    grid-template-columns: clamp(210px, var(--left-panel-width, 248px), 36vw) minmax(0, 1fr) 164px;
    gap: 12px;
  }

  .center-title h2 {
    font-size: 20px;
  }

  .nav-item {
    font-size: 14px;
  }

  :deep(.section-editor .el-textarea__inner) {
    min-height: calc(100vh - 350px) !important;
  }

  :deep(.synopsis-editor .el-textarea__inner) {
    min-height: calc(100vh - 430px) !important;
  }
}

@media (max-width: 1280px) {
  .slow-drama-detail {
    height: calc(100vh - 126px);
    min-height: 560px;
    grid-template-columns: clamp(200px, var(--left-panel-width, 230px), 38vw) minmax(0, 1fr) 146px;
    gap: 10px;
  }

  .center-header {
    height: 62px;
    padding: 0 12px;
  }

  .center-title h2 {
    font-size: 18px;
  }

  .center-title p {
    font-size: 12px;
  }

  .meta-strip {
    gap: 8px;
    padding: 10px 10px 0;
  }

  .meta-card {
    padding: 8px 10px;
  }

  .content-section {
    padding: 10px;
  }

  :deep(.section-editor .el-textarea__inner) {
    min-height: calc(100vh - 330px) !important;
    font-size: 15px;
  }

  :deep(.synopsis-editor .el-textarea__inner) {
    min-height: calc(100vh - 410px) !important;
  }
}

@media (max-width: 980px) {
  .slow-drama-detail {
    grid-template-columns: 1fr;
    height: auto;
    min-height: 0;
    gap: 10px;
    padding: 2px 0 10px;
  }

  .center-panel {
    order: 1;
    min-height: 520px;
  }

  .left-chat-panel {
    order: 2;
    min-height: 360px;
    width: 100% !important;
  }

  .resize-handle {
    display: none;
  }

  .resize-hint {
    display: none;
  }

  .right-nav-panel {
    order: 0;
    flex-direction: row;
    flex-wrap: wrap;
    align-self: stretch;
    position: static;
    padding: 10px;
    gap: 8px;
  }

  .nav-item {
    padding: 8px 10px;
    font-size: 13px;
  }

  .center-title h2 {
    font-size: 17px;
  }

  .meta-strip {
    grid-template-columns: 1fr;
  }

  :deep(.section-editor .el-textarea__inner) {
    min-height: 340px !important;
  }

  .synopsis-split {
    grid-template-columns: 1fr;
  }

  :deep(.synopsis-editor .el-textarea__inner) {
    min-height: 220px !important;
  }

  .segment-plan-wrap {
    overflow-y: auto;
    overflow-x: hidden;
  }

  .dialogue-script-wrap {
    overflow-y: auto;
    overflow-x: hidden;
  }

  .segment-plan-layout {
    height: auto;
    min-height: 100%;
  }

  .segment-left-pane,
  .segment-right-pane {
    width: 100%;
    max-width: none;
    max-height: none;
    flex: none;
    overflow: visible;
  }

  .segment-plan-list {
    overflow: visible;
    padding-right: 0;
  }

  .dialogue-layout {
    grid-template-columns: 1fr;
  }

  .dialogue-pane {
    overflow: visible;
  }
}

@media (max-width: 768px) {
  .slow-drama-detail {
    gap: 8px;
  }

  .center-header {
    height: auto;
    padding: 10px;
    gap: 8px;
    flex-wrap: wrap;
  }

  .center-title {
    order: 3;
    width: 100%;
    text-align: left;
  }

  .center-title h2 {
    font-size: 16px;
  }

  .meta-strip {
    padding: 8px 8px 0;
  }

  .content-section {
    padding: 8px;
  }

  .episode-card {
    padding: 10px;
  }

  .left-header {
    height: 48px;
    font-size: 15px;
  }

  .chat-body {
    padding: 10px;
  }

  .chat-input-wrap {
    padding: 8px;
  }

  :deep(.composer-input .el-textarea__inner) {
    min-height: 86px !important;
  }

  .msg-bubble {
    max-width: 94%;
    font-size: 12px;
  }

  :deep(.section-editor .el-textarea__inner) {
    min-height: 300px !important;
    font-size: 14px;
    line-height: 1.75;
  }

  :deep(.synopsis-editor .el-textarea__inner) {
    min-height: 180px !important;
  }

  .segment-left-pane {
    min-width: 0;
    max-width: none;
  }

  :deep(.dialogue-outline-editor .el-textarea__inner),
  :deep(.dialogue-script-editor .el-textarea__inner) {
    min-height: 140px !important;
    font-size: 16px;
  }


  :deep(.segment-plan-editor .el-textarea__inner) {
    min-height: 160px !important;
    font-size: 14px;
  }

  :deep(.segment-episode-editor .el-textarea__inner) {
    min-height: 140px !important;
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .slow-drama-detail {
    padding-bottom: 6px;
  }

  .right-nav-panel {
    padding: 8px;
  }

  .nav-item {
    font-size: 12px;
    padding: 6px 8px;
    border-radius: 8px;
  }

  .center-header {
    padding: 8px;
  }

  .back-btn,
  .send-btn {
    width: 100%;
  }

  .send-btn {
    align-self: stretch;
  }

  .chat-input-wrap {
    gap: 6px;
  }

  .composer-footer {
    height: auto;
    padding: 6px;
  }

  :deep(.section-editor .el-textarea__inner) {
    min-height: 260px !important;
    font-size: 13px;
  }

  :deep(.synopsis-editor .el-textarea__inner) {
    min-height: 160px !important;
  }

  .segment-plan-wrap {
    padding: 10px;
  }

  .dialogue-script-wrap {
    padding: 10px;
  }

  .segment-plan-footer {
    flex-wrap: wrap;
  }

  .segment-action-controls {
    width: 100%;
    justify-content: flex-start;
  }

  .segment-generate-btn {
    margin-left: 0;
    width: 100%;
  }

  .segment-save-btn {
    width: 100%;
  }

  .dialogue-ep-select {
    width: 100%;
  }
}

@media (max-height: 780px) {
  .slow-drama-detail {
    min-height: 0;
    height: calc(100vh - 118px);
  }

  :deep(.synopsis-editor .el-textarea__inner) {
    min-height: 150px !important;
  }

  :deep(.segment-plan-editor .el-textarea__inner) {
    min-height: 140px !important;
  }

  :deep(.segment-episode-editor .el-textarea__inner) {
    min-height: 110px !important;
  }

  :deep(.dialogue-outline-editor .el-textarea__inner),
  :deep(.dialogue-script-editor .el-textarea__inner) {
    min-height: 120px !important;
  }
}
</style>
